import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import useStyles from '../common/CommonStyle/CommonStyle';
import { DataGridPro, getGridDateOperators, getGridStringOperators, GridFilterPanel, GridToolbarColumnsButton, GridToolbarDensitySelector, LicenseInfo } from '@mui/x-data-grid-pro';
import { SupportedLanguageDetails } from '../common/SupportedLanguageDetails/SupportedLanguageDetails';
import GridTextLocalization from '../common/GridTextLocalization/GridTextLocalization';
import { ModuleAccessPermissionKey } from '../../Constants/ModuleAccessKey';
import { LoggedInUserDetails } from '../common/LoggedInUserDetails/LoggedInUserDetails';
import { Badge, Button, Chip, CircularProgress, CssBaseline, Grid, IconButton, Menu, MenuItem, MenuList, Paper, Snackbar, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Alert } from '@mui/lab';
import CommonGridPagination from '../common/CommonGridPagination/CommonGridPagination';
import { GlobalEdiApiConstants } from '../../Constants/GlobalEdiApiConstants';
import RefreshIcon from '@mui/icons-material/Refresh';
import { CommonService } from '../../services';
import { CustomFilters } from '../common/CustomFilters/CustomFilters';
import IconApproval from '@mui/icons-material/LibraryAddCheck';
import CancelIcon from '@mui/icons-material/Cancel';
import saveAs from 'file-saver';
import ViewIcon from '@mui/icons-material/Visibility';
import CommonBackDrop from '../common/BackDrop';
import EditIcon from '@mui/icons-material/EditOutlined';
import ImportIcon from '@mui/icons-material/SaveAlt';
import ExportIcon from '@mui/icons-material/Publish';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CustomReportIcon from '@mui/icons-material/Archive';
import CommonReportDownloadForMsg from '../common/CommonReportDownloadForMsg';
import FileUploadDialog from '../common/FileUploadDialog';
import { CheckCircle, FilterList } from '@mui/icons-material';
import CustomFilterComponent, { GetFilterableColumns } from '../common/CustomFilterComponent';
import { manageUserPreferences } from '../../services/manageUserPreferences';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import DeliveryConfirmationStatistics from '../PurchaseOrderForecastOutbound/DeliveryConfirmationStatistics/DeliveryConfirmationStatistics';
import IconPurchaseOrderPurchaseForecastOutbound from '@mui/icons-material/AssignmentReturn';
import IconMessage from '@mui/icons-material/Forum';
import ChipInfoList from '../common/ChipInfoList';
import CustomBreadCrumb from '../common/CustomBreadCrumb';
import TabIcon from '@mui/icons-material/Tab';
import IconOrder from '@mui/icons-material/Assignment';

const columnFieldsName = {
    erpOrgCode: 'organization.erpOrgCode',
    erpEntityCode: 'entity.erpEntityCode',
    orgName: 'organization.orgName',
    entityName: 'entity.entityName',
    bpCode: 'bpCode',
    bpType: 'bpType',
    msgId: 'msgId',
    testInd: 'testInd',
    poNo: 'poNo',
    poNoRef: 'poNoRef',
    poDate: 'poDate',
    poScheduleType: 'poScheduleType',
    buyerPartyCode: 'buyerPartyCode',
    latestDataSource: 'latestDataSource',
    buyerName: 'buyerName',
    buyerAddress: 'buyerAddress',
    buyerCity: 'buyerCity',
    buyerState: 'buyerState',
    buyerCountry: 'buyerCountry',
    buyerPostalCode: 'buyerPostalCode',
    buyerTelNo: 'buyerTelNo',
    buyerFaxNo: 'buyerFaxNo',
    buyerEmail: 'buyerEmail',
    supplierPartyCode: 'supplierPartyCode',
    supplierName: 'supplierName',
    supplierAddress: 'supplierAddress',
    supplierCity: 'supplierCity',
    supplierState: 'supplierState',
    supplierCountry: 'supplierCountry',
    supplierPostalCode: 'supplierPostalCode',
    supplierTelNo: 'supplierTelNo',
    supplierFaxNo: 'supplierFaxNo',
    supplierEmail: 'supplierEmail',
    currencyCode: 'currencyCode',
    supplierVatNum: 'supplierVatNum',
    approved: 'approved',
    approvedBy: 'approvedBy',
    approvedDate: 'approvedDate',
    msgCreatedDt: 'msgCreatedDt',
    deliveryRemarks: 'deliveryRemarks',
    msgStatusFlag: 'msgStatusFlag',
    msgStatusRemarks: 'msgStatusRemarks',
    note: 'note',
    lastMessageSentMode: 'lastMessageSentMode',
    lastMessageSentTime: 'lastMessageSentTime',
    attribute1: 'attribute1',
    attribute2: 'attribute2',
    attribute3: 'attribute3',
    attribute4: 'attribute4',
    attribute5: 'attribute5',
    attribute6: 'attribute6',
    attribute7: 'attribute7',
    attribute8: 'attribute8',
    attribute9: 'attribute9',
    attribute10: 'attribute10',
    apprEmailSent: 'apprEmailSent',
    companyCode: 'companyCode',
    plantCode: 'plantCode',
    createdBy: 'createdBy',
    createdTime: 'createdTime',
    updatedBy: 'updatedBy',
    updatedTime: 'updatedTime',
    fromMsgSentTime: 'fromMsgSentTime',
    toMsgSentTime: 'toMsgSentTime',
    poFromDate: 'poFromDate',
    poToDate: 'poToDate',
    poStatus: 'poStatus',
    dlvyCfmStatus: 'dlvyCfmStatus',
    incoterms: 'incoterms',
    purchasingGroup: 'purchasingGroup',
    paymentTerms: 'paymentTerms',
    createdById: 'createdById',
    amendmentCount: 'amendmentCount',
    cancellationCount: 'cancellationCount',
    fromUpdatedDate: 'fromUpdatedDate',
    toUpdatedDate: 'toUpdatedDate',
    wbsCode: 'wbsCode',
    projectName: 'projectName',
    ackStatus: 'ackStatus',
    deljitFlag: 'deljitFlag',
    originalPoStatus: 'originalPoStatus',
};

const NoChip = withStyles((theme) => ({
    root: {
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        width: '60%',
    },
}))(Chip);

const YesChip = withStyles((theme) => ({
    root: {
        color: theme.palette.success.main,
        borderColor: theme.palette.success.main,
        width: '60%',
    },
}))(Chip);

function PurchaseOrderForecastOutbound(props) {
    const classes = useStyles();
    const history = useHistory();
    const intl = useIntl();
    const theme = useTheme();
    const X_GRID_LICENSE_KEY = `${process.env.REACT_APP_X_GRID_LICENSE_KEY}`.toString();
    LicenseInfo.setLicenseKey(
        X_GRID_LICENSE_KEY,
    );
    const { ispurchaseforecast, isBusinessPartnerPortal } = props;
    const [chipInfoList, setChipInfoList] = useState([]);
    const selectedLanguage = useContext(SupportedLanguageDetails);
    const localtext = GridTextLocalization();
    const { loggedInUserInfo } = useContext(LoggedInUserDetails);
    const currentUser = CommonService.getLoggedInUserInfo();
    const moduleAccessKeyPermission = ModuleAccessPermissionKey();
    const apiUrlAndParamBodyConstants = GlobalEdiApiConstants();
    const mobileScreen = useMediaQuery(theme.breakpoints.down('md'));
    const poPfOutboundModuleAccess = loggedInUserInfo.roleModuleAccess[moduleAccessKeyPermission.outboundPurchaseOrders];
    const purchaseOrderBPModuleAccess = loggedInUserInfo.roleModuleAccess[moduleAccessKeyPermission.purchaseOrderBP];

    const pageLabelsConstantsAndMessages = {
        alignment: {
            alignLeft: 'left',
            alignRight: 'right',
            alignCenter: 'center',
        },
    }

    const [alertBoxState, setAlertBoxState] = React.useState({
        openAlert: false,
        vertical: 'top',
        horizontal: 'center',
        message: '',
        severity: ''
    });

    const handleCloseAlert = () => {
        setAlertBoxState({ ...alertBoxState, openAlert: false });
    };
    const { vertical, horizontal, openAlert, message, severity } = alertBoxState;

    const [refreshTable, setRefreshTable] = useState(false);
    const [openBackDrop, setOpenBackDrop] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [tableLoadingStatus, setTableLoadingStatus] = useState(false);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isRowSentForApproval, setIsRowSentForApproval] = useState(false);
    const [selectedRow, setSelectedRow] = React.useState([]);
    const [disableApproveButton, isDisableApproveButton] = useState(true);
    const [disableRejectionButton, isDisableRejectionButton] = useState(true);
    const [disiablingDownloadButtonForZeroRecords, setDisiablingDownloadButtonForZeroRecords] = useState(false);
    const [keyValueDataForDlvSchedule, setKeyValueDataForDlvSchedule] = useState({});
    var defaultEntity = loggedInUserInfo.defaultEntity;
    const [searchedEntityCode, setSearchedEntityCode] = useState(defaultEntity);
    const [searchedBpCode, setSearchedBpCode] = useState('');
    const [searchedCommType, setSearchedCommType] = useState('');
    const [searchedPoNo, setSearchedPoNo] = useState('');
    const [searchedStatusFlag, setSearchedStatusFlag] = useState('');
    const [searchedBuyerName, setSearchedBuyerName] = useState('');
    const [searchedSupplierName, setSearchedSupplierName] = useState('');
    const [searchedOrderCreatedBy, setSearchedOrderCreatedBy] = useState('');
    const [searchedPoFromDate, setSearchedPoFromDate] = useState('');
    const [searchedPoToDate, setSearchedPoToDate] = useState('');
    const [searchedMsgSentFromDate, setSeachedMessageSentFromDate] = useState('');
    const [searchedMsgSentToDate, setSeachedMessageSentToDate] = useState('');
    const [searchedAmendedValue, setSearchedAmendedValue] = useState('');
    const [searchedCancelledValue, setSearchedCancelledValue] = useState('');
    const [searchedDataSourceValue, setSearchedDataSourceValue] = useState('');
    const [searchedApprovedRecordsRecords, setSearchedApprovedRecordsRecords] = useState(null);
    const [isDlvScheduleForSapExportXlRequested, setDlvScheduleForSapExportXlRequested] = useState(false);
    const [isExportXlRequested, setExportXlRequested] = useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [selectedIndexForAction, setSelectedIndexForAction] = React.useState(0);

    const anchorRef = React.useRef(null);
    const [searchedPoStatus, setSearchedPoStatus] = useState('');
    const [searchedDeliveryConfirmed, setSearchedDeliveryConfirmed] = useState(null);
    const [openFileUploadDialog, setOpenFileUploadDialog] = useState(false);
    const [searchedUpdatedFromTime, setSearchedUpdatedFromTime] = useState(null);
    const [searchedUpdatedToTime, setSearchedUpdatedToTime] = useState('');
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [anchorE2, setAnchorE2] = React.useState(null);
    const [selectedRevisionList, setSelectedRevisionList] = useState([]);
    const [selectedRevisionBatchId, setSelectedRevisionBatchId] = useState('');
    const [selectedRevisionPoNo, setSelectedRevisionPoNo] = useState('');
    const [selectedRevisionMsgId, setSelectedRevisionMsgId] = useState('');
    const [auditViewAmendmentValue, setAuditViewAmendmentValue] = useState('');
    const [searchedWbsCode, setSearchedWbsCode] = useState('');
    const [searchedWbsName, setSearchedWbsName] = useState('');
    const [openFilterPanel, setFilterPanel] = useState(false);
    const [filterCount, setFilterCount] = React.useState(0);
    const [openDlvyCfmStatDialog, setOpenDlvyCfmStatDialog] = useState(false);
    const [selectedCancellationList, setSelectedCancellationList] = useState([]);
    const [selectedCancellationBatchId, setSelectedCancellationBatchId] = useState('');
    const [selectedCancellationMsgId, setSelectedCancellationMsgId] = useState('');
   const[searchedDeljitCount,setSearchedDeljitValue] = useState('');
   const [searchedOriginalPoStatus, setSearchedOriginalPoStatus] = useState('');
    const userAccessBpCode = currentUser.userSupplierAccessList === null ? null : currentUser.userSupplierAccessList[0].supplier.bpCode;
    const userAccessBpName = currentUser.userSupplierAccessList === null ? null : currentUser.userSupplierAccessList[0].supplier.bpSupplierName;

    const dateValue = new Date();

    const [paramBody, setParamBody] = useState(
        {
            body: {
                entity: {
                    erpEntityCode: defaultEntity,
                    entityName: ''
                },
                approved: '',
                poScheduleType: "1",
                recordStatusFlag: 'All',
                recordApprovedStatus: 'All',
                recordCancellationCount: 'All',
                recordAmendmentCount: 'All',
                fromUpdatedDate: isBusinessPartnerPortal ? null : new Date(dateValue.setDate(dateValue.getDate() - 7)).toISOString().slice(0, 10),
                poStatus: isBusinessPartnerPortal ? 'IN-COMPLETE' : null,
                dlvyCfmStatus: isBusinessPartnerPortal ? 'NOT/PARTIAL CONFIRMED' : null,
                recordDeljitFlg: 'All',
            },
            pageNo: 0,
            sortDir: 'desc',
            sortKey: 'updatedTime',
            recordsPerPage: 25,
        }
    );

    const breadCrumbForMessageElement =
    {
        path: undefined,
        name: <FormattedMessage id="common.menu.breadcrumb.message" />,
        iconName: IconMessage,
    };
    const breadCrumbForPurchaseOrderForecastElement =
    {
        path: '/purchaseOrdersOutbound',
        name: <FormattedMessage id="common.module.breadcrumb.purchaseorderpurchaseforecastoutbound" defaultMessage='Purchase Order' />,
        iconName: IconPurchaseOrderPurchaseForecastOutbound,
    };

    const breadCrumbForOrdersElement =
    {
        path: '/orders',
        name: <FormattedMessage id="order.breadcrumb.text" defaultMessage='Orders' />,
        iconName: TabIcon,
    };

    const breadCrumbForPoElementForBP = {
        path: undefined,
        name: <FormattedMessage id="purchaseorder.module.text" defaultMessage='Purchase Orders' />,
        iconName: IconOrder,
    };

    const [componentList, setComponentList] = useState((() => {
        let constructedBaseBreadCrumb = [];
        if (!isBusinessPartnerPortal) {
            constructedBaseBreadCrumb = [breadCrumbForMessageElement, breadCrumbForPurchaseOrderForecastElement,
            ]
            return constructedBaseBreadCrumb;
        } else if (isBusinessPartnerPortal) {
            constructedBaseBreadCrumb = [breadCrumbForOrdersElement,
                breadCrumbForPoElementForBP]
            return constructedBaseBreadCrumb;
        }
    }));

    const handleCloseForFilter = () => {
        setFilterPanel(false);
    }

    const defaultFilterForGlobalEdiWithoutEntityFilter = [
        { columnField: columnFieldsName.fromUpdatedDate, headerName: 'Updated From Date', id: 0, value: new Date(dateValue.setDate(dateValue.getDate() - 0)).toISOString().slice(0, 10) },
    ];

    const defaultFilterForGlobalEdi = [
        { columnField: columnFieldsName.fromUpdatedDate, headerName: 'Updated From Date', id: 0, value: new Date(dateValue.setDate(dateValue.getDate() - 0)).toISOString().slice(0, 10) },
        { columnField: columnFieldsName.erpEntityCode, headerName: 'Ent. Code', id: 1, value: defaultEntity }
    ];

    const defaultFilterForBpPODate = [
        { columnField: columnFieldsName.erpEntityCode, headerName: 'Ent. Code', id: 0, value: defaultEntity }
    ];

    const defaultFilterForBP = [
        { columnField: columnFieldsName.dlvyCfmStatus, headerName: 'Dlv. Cfm Status', id: 1, value: 'NOT/PARTIAL CONFIRMED' },
        { columnField: columnFieldsName.poStatus, headerName: 'PO Dlv. Status', id: 2, value: 'In-Complete' }
    ];

    const defaultFilterForBPWithoutEntityFilter = [
        { columnField: columnFieldsName.dlvyCfmStatus, headerName: 'Dlv. Cfm Status', id: 0, value: 'NOT/PARTIAL CONFIRMED' },
        { columnField: columnFieldsName.poStatus, headerName: 'PO Dlv. Status', id: 1, value: 'In-Complete' }
    ];

    const defaultFilterOptionswithDefaultEntityFilter = isBusinessPartnerPortal ? defaultFilterForBpPODate.concat(defaultFilterForBP) : defaultFilterForGlobalEdi;

    const defaultFilterOptionswithoutDefaultEntityFilter = isBusinessPartnerPortal ? defaultFilterForBPWithoutEntityFilter : defaultFilterForGlobalEdiWithoutEntityFilter;

    const defaulSelectedFilters = defaultEntity === '' ? defaultFilterOptionswithoutDefaultEntityFilter : defaultFilterOptionswithDefaultEntityFilter;

    const [selectedFilters, setSelectedFilters] = useState(defaulSelectedFilters);

    const [openReportDownloadDialog, setOpenReportDownloadDialog] = useState(false);
    const [proccessValue, setProcessValue] = useState('');
    const [bpCodeValue, setBpCodeValue] = useState('');
    const [entityValue, setEntityValue] = useState('');
    const [messageIdvalue, setMessageIdValue] = useState('');

    const closeReportDownloadDialog = (confirmed) => {
        setOpenReportDownloadDialog(false);
    }

    const closeFileUploadDialog = (confirmed) => {
        setOpenFileUploadDialog(false);
    }

    const closeDlvyCfmStatDialog = (confirmed) => {
        setOpenDlvyCfmStatDialog(false);
    }

    const [callSaveApiForOrderVisibility, setCallSaveApiForOrderVisibility] = useState(false);
    const [callSaveApiForSorting, setCallSaveApiForSorting] = useState(false);
    const [preference, setPreference] = useState(apiUrlAndParamBodyConstants.paramBodyForGetListApis.userPreference);
    const [pinnedColumns, setPinnedColumns] = useState({
        left: ["__check__"],
        right: []
    });
    const [callSaveApiForPinning, setCallSaveApiForPinning] = useState(false);
    const [callSaveApiForFilter, setCallSaveApiForFilter] = useState(false);
    const [intialParamBodyApiCall, setIntialParamBodyApiCall] = useState(false);

    function orderAndHideColumns(savedUserPreference, defaultUserPreference) {
        if (savedUserPreference.length !== 0 && defaultUserPreference.length !== 0) {
            var orderedUserPreferences = [],
                len = defaultUserPreference.length,
                len_copy = len,
                index, current;

            for (; len--;) {
                current = defaultUserPreference[len];
                index = savedUserPreference.findIndex(element => element.field === current.field);
                orderedUserPreferences[index] = current;
                orderedUserPreferences[index].hide = savedUserPreference[index].hide;
            }

            Array.prototype.splice.apply(defaultUserPreference, [0, len_copy].concat(orderedUserPreferences));
            return orderedUserPreferences;
        }
    }

    const savePreference = (preferenceChangeIn, params, orderedColumns) => {
        if (preferenceChangeIn === "filter") {
            setCallSaveApiForFilter(true);
            setPreference(prev => ({ ...prev, columnsFilter: params }));
        }
        else if (preferenceChangeIn === "sort") {
            setCallSaveApiForSorting(true);
            setPreference(prev => ({ ...prev, columnsSort: { field: params.field, sort: params.sort } }));
        }
        else if (preferenceChangeIn === "visibility" || preferenceChangeIn === "order") {
            var splicedFirstColumn = params;
            if (params[0].field === "__check__")
                splicedFirstColumn = params.splice(1, params.length);
            setCallSaveApiForOrderVisibility(true);
            setPreference(prev => ({ ...prev, columnsOrdervisible: splicedFirstColumn }));
        }
        else if (preferenceChangeIn === "pinned") {
            setCallSaveApiForPinning(true);
            setPreference(prev => ({ ...prev, columnsOrdervisible: orderedColumns, columnsPinned: params }));
        }
    }

    useEffect(() => {
        manageUserPreferences.saveLastOpenedScreen(componentList[1].name.props.id)
            .then((response) => { })
            .catch(function (error) {
                if (error.response !== undefined) {
                    setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response.data.message, severity: "error" });
                }
            });

    }, []);

    useEffect(() => {
        if (callSaveApiForPinning === false)
            preference.columnsPinned = pinnedColumns;
        if (callSaveApiForFilter === false)
            preference.columnsFilter = selectedFilters;
        if (callSaveApiForOrderVisibility === false && callSaveApiForPinning === false)
            preference.columnsOrdervisible = columns;
        if (callSaveApiForSorting === false)
            preference.columnsSort = { field: paramBody.sortKey, sort: paramBody.sortDir };
        if ((callSaveApiForFilter === true || callSaveApiForPinning === true || callSaveApiForOrderVisibility === true || callSaveApiForSorting === true)
            && (preference.columnsOrdervisible !== undefined && preference.columnsOrdervisible !== null && preference.columnsOrdervisible?.length !== 0 && preference.columnsSort.field !== '')) {
            manageUserPreferences.saveGridPreferences({
                userId: 0,
                organization: {},
                entity: {},
                screenName: componentList[1].name.props.id,
                tabName: componentList[1].name.props.id,
                preferences: JSON.stringify(preference),
                lastOpen: true,
            }).then((response) => {
                if (callSaveApiForOrderVisibility === true) {
                    var defaultColumns = columns;
                    var userChangedColumns = preference.columnsOrdervisible;
                    var userPreferedColumns = orderAndHideColumns(userChangedColumns, defaultColumns);
                    setColumns(userPreferedColumns);
                    setCallSaveApiForOrderVisibility(false);
                }
                else if (callSaveApiForSorting === true) {
                    setCallSaveApiForSorting(false);
                }
                else if (callSaveApiForPinning === true) {
                    setPinnedColumns(preference.columnsPinned);
                    setColumns(preference.columnsOrdervisible);
                    setCallSaveApiForPinning(false);
                }
                else if (callSaveApiForFilter === true) {
                    setCallSaveApiForFilter(false);
                }
            })
                .catch(function (error) {
                    if (error.response !== undefined) {
                        setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response.data.message, severity: "error" });
                    }
                });
        }
    }, [preference]);

    const commonColumnForPOPF = [
        {
            field: 'action',
            headerName: intl.formatMessage({ id: "columnname.action.label" }),
            width: isBusinessPartnerPortal ? 100 : 100,
            sortable: false,
            hide: false,
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns,
            renderCell: (cellParams) => (
                <div>
                    {(poPfOutboundModuleAccess !== undefined ? poPfOutboundModuleAccess.readFlag && !(poPfOutboundModuleAccess.editFlag || poPfOutboundModuleAccess.createFlag) : false) && (!isBusinessPartnerPortal) && (
                        <Tooltip title={<FormattedMessage id="common.button.view.breadcrumb.tooltip" />}
                            placement='bottom' className={classes.actionButton} onClick={(() => {
                                let msgId = cellParams.row.msgId;
                                history.push(`${apiUrlAndParamBodyConstants.commonApiUrlForAllTheScreen.poPfOutbound}/${msgId}`,
                                    {
                                        mode: 'view', msgId: msgId,
                                        isBusinessPartnerPortal: isBusinessPartnerPortal,
                                        ispurchaseforecast: ispurchaseforecast,
                                        userAccessBpCode: userAccessBpCode,
                                        userAccessBpName: userAccessBpName,
                                        defaultEntity: defaultEntity, defaultBpType: "SUPPLIER"
                                    });
                            })}>
                            <span>
                                <IconButton className={classes.customeToolbar} size="large">
                                    <ViewIcon />
                                </IconButton>
                            </span>
                        </Tooltip>)}
                    {(poPfOutboundModuleAccess !== undefined ? poPfOutboundModuleAccess.editFlag || poPfOutboundModuleAccess.createFlag : false)
                        && (!isBusinessPartnerPortal) && (
                            <Tooltip title={<FormattedMessage id="common.tooltip.breadcrumb.edit" />}
                                placement='bottom' className={classes.actionButton} onClick={(() => {
                                    let msgId = cellParams.row.msgId;
                                    history.push(`${apiUrlAndParamBodyConstants.commonApiUrlForAllTheScreen.poPfOutbound}/${msgId}`, {
                                        mode: 'edit', msgId: msgId,
                                        isBusinessPartnerPortal: isBusinessPartnerPortal,
                                        ispurchaseforecast: ispurchaseforecast,
                                        userAccessBpCode: userAccessBpCode, userAccessBpName: userAccessBpName,
                                        defaultEntity: defaultEntity, defaultBpType: "SUPPLIER"
                                    });
                                })}>
                                <span>
                                    <IconButton className={classes.customeToolbar} size="large">
                                        <EditIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>)}
                    {(purchaseOrderBPModuleAccess !== undefined ? purchaseOrderBPModuleAccess.editFlag || purchaseOrderBPModuleAccess.createFlag : false) && (isBusinessPartnerPortal) && (
                        <Tooltip title={<FormattedMessage id="common.tooltip.breadcrumb.edit" />}
                            placement='bottom' className={classes.actionButton} onClick={(() => {
                                let msgId = cellParams.row.msgId;
                                history.push(`${apiUrlAndParamBodyConstants.commonApiUrlForAllTheScreen.poPfOutbound}/${msgId}`, {
                                    mode: 'edit', msgId: msgId,
                                    isBusinessPartnerPortal: isBusinessPartnerPortal,
                                    ispurchaseforecast: ispurchaseforecast,
                                    userAccessBpCode: userAccessBpCode, userAccessBpName: userAccessBpName, defaultEntity: defaultEntity, defaultBpType: "SUPPLIER"
                                });
                            })}>
                            <span>
                                <IconButton className={classes.customeToolbar} size="large">
                                    <EditIcon />
                                </IconButton>
                            </span>
                        </Tooltip>)}
                    {(purchaseOrderBPModuleAccess !== undefined ? purchaseOrderBPModuleAccess.editFlag === true || purchaseOrderBPModuleAccess.createFlag === true ? false : purchaseOrderBPModuleAccess.readFlag : false) && (isBusinessPartnerPortal) && (
                        <Tooltip title={<FormattedMessage id="common.button.view.breadcrumb.tooltip" />}
                            placement='bottom' className={classes.actionButton} onClick={(() => {
                                let msgId = cellParams.row.msgId;
                                history.push(`${apiUrlAndParamBodyConstants.commonApiUrlForAllTheScreen.poPfOutbound}/${msgId}`, {
                                    mode: 'view', msgId: msgId,
                                    isBusinessPartnerPortal: isBusinessPartnerPortal,
                                    ispurchaseforecast: ispurchaseforecast,
                                    userAccessBpCode: userAccessBpCode, userAccessBpName: userAccessBpName, defaultEntity: defaultEntity, defaultBpType: "SUPPLIER"
                                });
                            })}>
                            <span>
                                <IconButton className={classes.customeToolbar} size="large">
                                    <ViewIcon />
                                </IconButton>
                            </span>
                        </Tooltip>)}
                </div >
            )
        },
        {
            field: columnFieldsName.erpEntityCode,
            headerName: intl.formatMessage({ id: "columnname.entitycode.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            width: 85,
            sortable: false,
            type: 'string',
            hide: false,
            filterable: true,
            valueGetter: (params) => `${params.row.entity.erpEntityCode}`,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.bpCode,
            headerName: intl.formatMessage({ id: "common.textfield.columname.bpcode" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            width: 130,
            sortable: true,
            type: 'string',
            hide: isBusinessPartnerPortal ? true : false,
            filterable: true,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.supplierName,
            width: 300,
            headerName: intl.formatMessage({ id: "columnName.bpname.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: isBusinessPartnerPortal ? true : false,
            filterable: true,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.poNo,
            headerName: intl.formatMessage({ id: "columnname.pono.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            width: 110,
            sortable: true,
            type: 'string',
            hide: false,
            filterable: true,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.wbsCode,
            headerName: intl.formatMessage({ id: "common.textfield.column.project" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            width: 90,
            sortable: true,
            type: 'string',
            hide: false,
            filterable: true,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.projectName,
            headerName: intl.formatMessage({ id: "common.textfield.column.projectName" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            width: 110,
            sortable: true,
            type: 'string',
            hide: false,
            filterable: true,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.poNoRef,
            headerName: intl.formatMessage({ id: "columnname.ponoref.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            width: 150,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.poDate,
            headerName: intl.formatMessage({ id: "columnname.podate.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            width: 130,
            sortable: true,
            type: 'string',
            hide: false,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns,
            renderCell: (cellParams) => (<span>{cellParams.row.poDateDisp}</span>)
        },
        {
            field: columnFieldsName.poScheduleType,
            headerName: intl.formatMessage({ id: "columnname.poscheduletype.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            width: 190,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns,
            renderCell: ((cellParams) => {
                if (cellParams.row.poScheduleType === "1") {
                    return 'FIRM'
                } else if (cellParams.row.poScheduleType === "4") {
                    return 'FORECAST'
                }
            })
        },
        {
            field: columnFieldsName.latestDataSource,
            width: 170,
            headerName: intl.formatMessage({ id: "columnname.latestDataSource.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: true,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.buyerName,
            width: 170,
            headerName: intl.formatMessage({ id: "columnname.buyersName.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: true,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.buyerAddress,
            width: 170,
            headerName: intl.formatMessage({ id: "columnname.buyerAddress.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.buyerCity,
            width: 150,
            headerName: intl.formatMessage({ id: "columnname.buyerCity.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.buyerState,
            width: 150,
            headerName: intl.formatMessage({ id: "columnname.buyerState.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.buyerCountry,
            width: 165,
            headerName: intl.formatMessage({ id: "columnname.buyerCountry.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.buyerPostalCode,
            width: 190,
            headerName: intl.formatMessage({ id: "columnname.buyerPostalCode.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.buyerTelNo,
            width: 180,
            headerName: intl.formatMessage({ id: "columnname.buyerTelNo.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.buyerFaxNo,
            width: 180,
            headerName: intl.formatMessage({ id: "columnname.buyerFaxNo.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.buyerEmail,
            width: 180,
            headerName: intl.formatMessage({ id: "columnname.buyerEmail.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.supplierAddress,
            width: 185,
            headerName: intl.formatMessage({ id: "columnname.supplierAddress.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.supplierCity,
            width: 160,
            headerName: intl.formatMessage({ id: "columnname.supplierCity.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.supplierState,
            width: 160,
            headerName: intl.formatMessage({ id: "columnname.supplierState.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.supplierCountry,
            width: 180,
            headerName: intl.formatMessage({ id: "columnname.supplierCountry.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.supplierPostalCode,
            width: 220,
            headerName: intl.formatMessage({ id: "columnname.supplierPostalCode.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.supplierTelNo,
            width: 180,
            headerName: intl.formatMessage({ id: "columnname.supplierTelNo.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.supplierFaxNo,
            width: 180,
            headerName: intl.formatMessage({ id: "columnname.supplierFaxNo.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.supplierEmail,
            width: 180,
            headerName: intl.formatMessage({ id: "columnname.supplierEmail.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.currencyCode,
            width: 170,
            headerName: intl.formatMessage({ id: "columnname.curCd.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: isBusinessPartnerPortal ? true : false,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.supplierVatNum,
            width: 170,
            headerName: intl.formatMessage({ id: "columnname.supplierVatNumber.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.note,
            headerName: intl.formatMessage({ id: "columnname.note.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 140,
            sortable: true,
            type: 'string',
            filterable: false,
            hide: true,
            renderHeader: CommonService.customRenderHeaderColumns,
        },
        {
            field: columnFieldsName.incoterms,
            headerName: intl.formatMessage({ id: "columnname.incoTerms.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 140,
            sortable: true,
            type: 'string',
            filterable: false,
            hide: true,
            renderHeader: CommonService.customRenderHeaderColumns,
        },
        {
            field: columnFieldsName.purchasingGroup,
            headerName: intl.formatMessage({ id: "columnname.purchasingGroup.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 185,
            sortable: true,
            type: 'string',
            filterable: false,
            hide: true,
            renderHeader: CommonService.customRenderHeaderColumns,
        },
        {
            field: columnFieldsName.paymentTerms,
            headerName: intl.formatMessage({ id: "columnname.paymentTerms.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 170,
            sortable: true,
            type: 'string',
            filterable: false,
            hide: true,
            renderHeader: CommonService.customRenderHeaderColumns,
        },
        {
            field: columnFieldsName.companyCode,
            headerName: intl.formatMessage({ id: "columnname.companycode.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 180,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.plantCode,
            headerName: intl.formatMessage({ id: "columnname.plantcode.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 150,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.updatedBy,
            headerName: intl.formatMessage({ id: "columnname.updatedby.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            width: 155,
            sortable: true,
            type: 'string',
            hide: false,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.updatedTime,
            headerName: intl.formatMessage({ id: "columnname.updateddate.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            width: 180,
            sortable: true,
            type: 'string',
            hide: false,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns,
            renderCell: (cellParams) => (<span>{cellParams.row.updatedTimeDisp}</span>)
        },
        {
            field: columnFieldsName.createdTime,
            headerName: intl.formatMessage({ id: "columnname.createddate.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
            width: 180,
            sortable: true,
            type: 'string',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns,
            renderCell: (cellParams) => (<span>{cellParams.row.createdTimeDisp}</span>)
        },
        {
            field: columnFieldsName.attribute1,
            headerName: intl.formatMessage({ id: "columnname.attribute1.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 150,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.attribute2,
            headerName: intl.formatMessage({ id: "columnname.attribute2.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 150,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.attribute3,
            headerName: intl.formatMessage({ id: "columnname.attribute3.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 150,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.attribute4,
            headerName: intl.formatMessage({ id: "columnname.attribute4.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 150,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.attribute5,
            headerName: intl.formatMessage({ id: "columnname.attribute5.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 150,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.attribute6,
            headerName: intl.formatMessage({ id: "columnname.attribute6.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 150,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.attribute7,
            headerName: intl.formatMessage({ id: "columnname.attribute7.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 150,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.attribute8,
            headerName: intl.formatMessage({ id: "columnname.attribute8.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 150,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.attribute9,
            headerName: intl.formatMessage({ id: "columnname.attribute9.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 150,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.attribute10,
            headerName: intl.formatMessage({ id: "columnname.attribute10.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 150,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: false,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.poFromDate,
            headerName: intl.formatMessage({ id: "columnname.pofromdate.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 0,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: true,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.poToDate,
            headerName: intl.formatMessage({ id: "columnname.potodate.label" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 0,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: true,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.fromUpdatedDate,
            headerName: intl.formatMessage({ id: "columnname.updatedFromDate.text" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 0,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: true,
            renderHeader: CommonService.customRenderHeaderColumns
        },
        {
            field: columnFieldsName.toUpdatedDate,
            headerName: intl.formatMessage({ id: "columnname.updatedToDate.text" }),
            headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
            width: 0,
            sortable: false,
            type: 'dateTime',
            hide: true,
            filterable: true,
            renderHeader: CommonService.customRenderHeaderColumns
        },
    ];

    const purchaseOrderPoAmended = {
        field: 'amendmentCount',
        width: 170,
        headerName: intl.formatMessage({ id: "columnname.poamended.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        sortable: true,
        filterable: true,
        type: 'string',
        hide: false,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: ((cellParams) => {
            if (cellParams.row.amendmentCount > 0) {
                return (<YesChip
                    icon={<CheckCircle />}
                    variant="outlined"
                    label='YES'
                    color='primary'
                />)
            } else {
                return (<NoChip
                    icon={<CancelIcon />}
                    variant="outlined"
                    label='NO'
                    color='secondary'
                />)
            }
        }
        )
    };

    const purchaseOrderPoCancelled = {
        field: 'cancellationCount',
        width: 170,
        headerName: intl.formatMessage({ id: "columnname.pocancancelled.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        sortable: true,
        type: 'string',
        filterable: true,
        hide: false,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: ((cellParams) => {
            if (cellParams.row.cancellationCount > 0) {
                return (<YesChip
                    icon={<CheckCircle />}
                    variant="outlined"
                    label='YES'
                    color='primary'
                />)
            } else {
                return (<NoChip
                    icon={<CancelIcon />}
                    variant="outlined"
                    label='NO'
                    color='secondary'
                />)
            }
        }
        )
    };

    const columnsForPOPFApproved = {
        field: columnFieldsName.approved,
        headerName: intl.formatMessage({ id: "columnName.approved.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        width: 150,
        sortable: true,
        type: 'string',
        hide: false,
        filterable: true,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: CommonService.ApproveFailureChipsForPO
    };

    const columnsForPOPFApprovedBy = {
        field: columnFieldsName.approvedBy,
        width: 155,
        headerName: intl.formatMessage({ id: "columnName.approvedBy.label" }),
        sortable: true,
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        type: 'string',
        hide: true,
        filterable: false,
        renderHeader: CommonService.customRenderHeaderColumns,
    };

    const columnsForPOPFApprovedDate = {
        field: columnFieldsName.approvedDate,
        width: 170,
        headerName: intl.formatMessage({ id: "columnName.approvedDate.label" }),
        sortable: true,
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        type: 'dateTime',
        hide: true,
        filterable: false,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: (cellParams) => (<span>{cellParams.row.approvedDateDisp}</span>)
    };

    const columnsForPOPFApprEmailSent = {
        field: columnFieldsName.apprEmailSent,
        width: 170,
        headerName: intl.formatMessage({ id: "columnName.emailSent.label" }),
        sortable: true,
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
        type: 'string',
        hide: true,
        filterable: false,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: CommonService.customRenderStatusColumnForApprEmailSent
    };

    const columnsForPOPFStatusFlag = {
        field: columnFieldsName.msgStatusFlag,
        width: 180,
        headerName: intl.formatMessage({ id: "columnName.msgSentStatus.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        sortable: true,
        type: 'string',
        hide: false,
        filterable: true,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: CommonService.PoStatusForOutChips
    };

    const columnsForPOPFMsgStatusRemarks = {
        field: columnFieldsName.msgStatusRemarks,
        width: 245,
        headerName: intl.formatMessage({ id: "columnName.msgSentStatusRemarks.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        sortable: true,
        type: 'string',
        hide: true,
        filterable: false,
        renderHeader: CommonService.customRenderHeaderColumns,
    };

    const columnsForPOPFMessageSentMode = {
        field: columnFieldsName.lastMessageSentMode,
        headerName: intl.formatMessage({ id: "columnname.lastMessageSentMode.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        width: 180,
        sortable: true,
        type: 'string',
        hide: true,
        filterable: true,
        renderHeader: CommonService.customRenderHeaderColumns
    };

    const columnsForPOPFLastMessageSentTime = {
        field: columnFieldsName.lastMessageSentTime,
        headerName: intl.formatMessage({ id: "columnname.lastMessageSentTime.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        width: 190,
        sortable: true,
        type: 'string',
        hide: true,
        filterable: false,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: (cellParams) => (<span>{cellParams.row.msgSentTimeDisp}</span>)
    };

    const columnForPurchaseOrderDelStatus = {
        field: columnFieldsName.dlvyCfmStatus,
        width: 170,
        headerName: intl.formatMessage({ id: "columnname.delCfmStatus.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        sortable: true,
        type: 'string',
        hide: false,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: CommonService.DelCfmStatusDisplayField
    };

    const columnForPurchaseOrderPOStatus = {
        field: columnFieldsName.poStatus,
        width: 170,
        headerName: intl.formatMessage({ id: "columnname.postatus.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        sortable: true,
        type: 'string',
        hide: false,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: CommonService.PoStatusDisplayField
    };

    const columnForPurchaseOrderMsgSentFromTime = {
        field: columnFieldsName.fromMsgSentTime,
        headerName: intl.formatMessage({ id: "columnname.frommessagesentdate.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        width: 150,
        sortable: false,
        type: 'dateTime',
        hide: true,
        filterable: true,
        renderHeader: CommonService.customRenderHeaderColumns
    };

    const columnForPurchaseOrderMsgSentToTime = {
        field: columnFieldsName.toMsgSentTime,
        headerName: intl.formatMessage({ id: "columnname.tomessagesentdate.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        width: 150,
        sortable: false,
        type: 'dateTime',
        hide: true,
        filterable: true,
        renderHeader: CommonService.customRenderHeaderColumns
    };

    const columnForOrderCreatedBy = {
        field: columnFieldsName.createdBy,
        width: 190,
        headerName: intl.formatMessage({ id: "columnname.columnnamecreatedby.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignLeft}`,
        sortable: true,
        type: 'string',
        hide: false,
        filterable: true,
        renderHeader: CommonService.customRenderHeaderColumns
    };

    const columnForOrderCreatedById = {
        field: columnFieldsName.createdById,
        headerName: intl.formatMessage({ id: "columnname.createdById.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        width: 200,
        sortable: true,
        type: 'string',
        filterable: false,
        hide: true,
        renderHeader: CommonService.customRenderHeaderColumns,
    };

    const columnForPurchaseOrderAckStatus = {
        field: columnFieldsName.ackStatus,
        width: 180,
        headerName: intl.formatMessage({ id: "columnname.ackStatus.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        sortable: true,
        type: 'string',
        hide: false,
        filterable: false,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: CommonService.AckStatusDisplayField
    };

    const columnForPurchaseOrderDeljitFlag = {
        field: 'deljitFlag',
        width: 170,
        headerName: intl.formatMessage({ id: "columnname.deljit.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        sortable: true,
        filterable: true,
        type: 'string',
        hide: false,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: ((cellParams) => {
            if (cellParams.row.deljitFlag === 'X' || cellParams.row.deljitFlag === 'x') {
                return (<YesChip
                    icon={<CheckCircle />}
                    variant="outlined"
                    label='YES'
                    color='primary'
                />)
            } else if (cellParams.row.deljitFlag === null || cellParams.row.deljitFlag === '') {
                return (<NoChip
                    icon={<CancelIcon />}
                    variant="outlined"
                    label='NO'
                    color='secondary'
                />)
            }
        }
        )
    };

    const columnForOriginalPoStatus = {
        field: columnFieldsName.originalPoStatus,
        width: 180,
        headerName: intl.formatMessage({ id: "columnName.originalPoStatus.label" }),
        headerAlign: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        align: `${pageLabelsConstantsAndMessages.alignment.alignCenter}`,
        sortable: true,
        type: 'string',
        hide: false,
        filterable: true,
        renderHeader: CommonService.customRenderHeaderColumns,
        renderCell: CommonService.OriginalPoStatusChips
    };
    const insert = (arr, index, newItems) => [
        ...arr.slice(0, index),
        newItems,
        ...arr.slice(index)
    ]

    function constructColumnsToBeDisplayed() {
        var commonColumnWithPOPFEdiAndBp = [];
        const commonColumnWithPoDelStatus = insert(commonColumnForPOPF, 11, columnForPurchaseOrderDelStatus)
        const commonColumnWithPoStatus = insert(commonColumnWithPoDelStatus, 12, columnForPurchaseOrderPOStatus)
        const commonColumnWithPoAmended = insert(commonColumnWithPoStatus, 13, purchaseOrderPoAmended)
        const commonColumnWithPoCancelled = insert(commonColumnWithPoAmended, 14, purchaseOrderPoCancelled)
        const commonColumnForOrderCreatedBy = insert(commonColumnWithPoCancelled, 15, columnForOrderCreatedBy)
        const commonColumnWithAckStatus = insert(commonColumnForOrderCreatedBy, 23, columnForPurchaseOrderAckStatus)
        const commonColumnWithDeljitFlag = insert(commonColumnWithAckStatus,17, columnForPurchaseOrderDeljitFlag)
        commonColumnWithPOPFEdiAndBp = insert(commonColumnWithDeljitFlag, 16, columnForOrderCreatedById)
        return commonColumnWithPOPFEdiAndBp;
    }

    function constructColumnsToBeDisplayedForEdi() {
        var commonColumnWithPOPFEdiAndBp = [];
        const commonColumnWithPoDelStatus = insert(commonColumnForPOPF, 10, columnForPurchaseOrderDelStatus)
        const commonColumnWithPoStatus = insert(commonColumnWithPoDelStatus, 11, columnForPurchaseOrderPOStatus)
        const commonColumnWithPoPfEdiApproved = insert(commonColumnWithPoStatus, 12, columnsForPOPFApproved)
        const commonColumnWithPoPfEdiApprovedBy = insert(commonColumnWithPoPfEdiApproved, 13, columnsForPOPFApprovedBy)
        const commonColumnWithPoPfEdiApprovedDt = insert(commonColumnWithPoPfEdiApprovedBy, 14, columnsForPOPFApprovedDate)
        const commonColumnWithPoPfEdiAppEmailSent = insert(commonColumnWithPoPfEdiApprovedDt, 15, columnsForPOPFApprEmailSent)
        const commonColumnWithPoPfEdiStatusFlag = insert(commonColumnWithPoPfEdiAppEmailSent, 16, columnsForPOPFStatusFlag)
        const commonColumnWithPoPfEdiMsgStatusRemarks = insert(commonColumnWithPoPfEdiStatusFlag, 17, columnsForPOPFMsgStatusRemarks)
        const commonColumnWithPoPfEdiMsgSentMode = insert(commonColumnWithPoPfEdiMsgStatusRemarks, 18, columnsForPOPFMessageSentMode)
        const commonColumnWithPoPfEdiMsgSentTime = insert(commonColumnWithPoPfEdiMsgSentMode, 19, columnsForPOPFLastMessageSentTime)
        const commonColumnWithPoAmended = insert(commonColumnWithPoPfEdiMsgSentTime, 20, purchaseOrderPoAmended)
        const commonColumnWithPoCancellation = insert(commonColumnWithPoAmended, 21, purchaseOrderPoCancelled)
        const commonColumnWithPoPfEdiMsgSentFromTime = insert(commonColumnWithPoCancellation, 62, columnForPurchaseOrderMsgSentFromTime)
        const commonColumnWithMsgSentToTime = insert(commonColumnWithPoPfEdiMsgSentFromTime, 63, columnForPurchaseOrderMsgSentToTime)
        const commonColumnForOrderCreatedBy = insert(commonColumnWithMsgSentToTime, 22, columnForOrderCreatedBy)
        const commonColumnWithAckStatus = insert(commonColumnForOrderCreatedBy, 23, columnForPurchaseOrderAckStatus)
        const commonColumnWithDeljitFlag = insert(commonColumnWithAckStatus,24, columnForPurchaseOrderDeljitFlag)
        const commonColumnWithOriginalPoStatus = insert(commonColumnWithDeljitFlag, 25, columnForOriginalPoStatus)
        commonColumnWithPOPFEdiAndBp = insert(commonColumnWithOriginalPoStatus, 28, columnForOrderCreatedById)
        return commonColumnWithPOPFEdiAndBp;
    }

    useEffect(() => {
        setIntialParamBodyApiCall(false);
        setTableLoadingStatus(true);
        var createColumnsForDifferentScreens = columns;

        manageUserPreferences.loadUserPreferences({
            body: {
                screenName: componentList[1].name.props.id,
                tabName: componentList[1].name.props.id,
                userId: 0,
                organization: {},
                entity: {}
            }

        }).then((response) => {
            setIntialParamBodyApiCall(true);
            if (response.data === null) {
                if (!isBusinessPartnerPortal) {
                    createColumnsForDifferentScreens = constructColumnsToBeDisplayedForEdi()
                } else {
                    createColumnsForDifferentScreens = constructColumnsToBeDisplayed();
                }
                setColumns(createColumnsForDifferentScreens);
                savePreference("visibility", createColumnsForDifferentScreens);
                paramBody.body.entity.erpEntityCode = defaultEntity;
                paramBody.body.fromUpdatedDate = isBusinessPartnerPortal ? null : new Date(dateValue.setDate(dateValue.getDate() - 7)).toISOString().slice(0, 10);
                paramBody.body.poStatus = isBusinessPartnerPortal ? 'IN-COMPLETE' : null;
                paramBody.body.dlvyCfmStatus = isBusinessPartnerPortal ? 'NOT/PARTIAL CONFIRMED' : null;
                setParamBody({ ...paramBody });
            }
            //preference present in db
            else {
                if (!isBusinessPartnerPortal) {
                    createColumnsForDifferentScreens = constructColumnsToBeDisplayedForEdi()
                } else {
                    createColumnsForDifferentScreens = constructColumnsToBeDisplayed();
                }
                var savedColumnsInResponse = JSON.parse(response.data.preferences);
                var orderedColumns = orderAndHideColumns(savedColumnsInResponse.columnsOrdervisible, createColumnsForDifferentScreens);
                setPinnedColumns(savedColumnsInResponse.columnsPinned);
                setColumns(orderedColumns);
                var previousParamBody = paramBody;

                //filter start
                let bpName = null;
                let bpCode = null;
                let entityCode = null;
                let buyerName = null;
                let artifactId = null;
                let createdBy = null;
                let msgStatusFlag = null;
                let approved = null;
                let poStatus = null;
                let dlvyCfmStatus = null;
                let lastMessageSentMode = null;
                let amendmentCount = null;
                let cancellationCount = null;
                let poFromDate = null;
                let poToDate = null;
                let fromMsgSentTime = null;
                let toMsgSentTime = null;
                let fromUpdatedDate = null;
                let toUpdatedDate = null;
                let latestDataSource = null;
                let wbsCode = null;
                let projectName = null;
                let deljitFlag = null;
                let originalPoStatus = null;
                savedColumnsInResponse.columnsFilter.map((columns) => {
                    switch (columns.columnField) {
                        case 'supplierName':
                            bpName = columns.value
                            break;
                        case 'createdBy':
                            createdBy = columns.value
                            break;
                        case 'poNo':
                            artifactId = columns.value
                            break;
                        case 'lastMessageSentMode':
                            lastMessageSentMode = columns.value
                            break;
                        case 'entity.erpEntityCode':
                            entityCode = columns.value
                            break;
                        case 'msgStatusFlag':
                            msgStatusFlag = columns.value
                            break;
                        case 'bpCode':
                            bpCode = columns.value
                            break;
                        case 'approved':
                            approved = columns.value
                            break;
                        case 'buyerName':
                            buyerName = columns.value
                            break;
                        case 'poFromDate':
                            poFromDate = columns.value
                            break;
                        case 'poToDate':
                            poToDate = columns.value
                            break;
                        case 'poStatus':
                            poStatus = columns.value
                            break;
                        case 'dlvyCfmStatus':
                            dlvyCfmStatus = columns.value
                            break;
                        case 'cancellationCount':
                            cancellationCount = columns.value
                            break;
                        case 'amendmentCount':
                            amendmentCount = columns.value
                            break;
                        case 'fromMsgSentTime':
                            fromMsgSentTime = columns.value
                            break;
                        case 'toMsgSentTime':
                            toMsgSentTime = columns.value
                            break;
                        case 'fromUpdatedDate':
                            fromUpdatedDate = columns.value
                            break;
                        case 'toUpdatedDate':
                            toUpdatedDate = columns.value
                            break;
                        case 'latestDataSource':
                            latestDataSource = columns.value
                            break;
                        case 'wbsCode':
                            wbsCode = columns.value
                            break;
                        case 'projectName':
                            projectName = columns.value
                            break;
                        case 'deljitFlag':
                            deljitFlag = columns.value
                            break;
                        case 'originalPoStatus':
                            originalPoStatus = columns.value
                            break;    
                    }
                })

                setSearchedEntityCode(entityCode);
                setSearchedPoNo(artifactId);
                setSearchedBpCode(bpCode);
                setSearchedApprovedRecordsRecords(approved);
                setSearchedCommType(lastMessageSentMode);
                setSearchedStatusFlag(msgStatusFlag);
                setSearchedBuyerName(buyerName);
                setSearchedSupplierName(bpName);
                setSearchedPoFromDate(poFromDate);
                setSearchedPoToDate(poToDate);
                setSeachedMessageSentFromDate(fromMsgSentTime);
                setSeachedMessageSentToDate(toMsgSentTime);
                setSearchedPoStatus(poStatus);
                setSearchedDeliveryConfirmed(dlvyCfmStatus);
                setSearchedOrderCreatedBy(createdBy);
                setSearchedAmendedValue(amendmentCount);
                setSearchedCancelledValue(cancellationCount);
                setSearchedUpdatedFromTime(fromUpdatedDate)
                setSearchedUpdatedToTime(toUpdatedDate);
                setSearchedDataSourceValue(latestDataSource);
                setSearchedWbsCode(wbsCode);
                setSearchedWbsName(projectName);
                setSearchedDeljitValue(deljitFlag);
                setSearchedOriginalPoStatus(originalPoStatus);

                var filterBody = {
                    entity: {
                        erpEntityCode: entityCode,
                    },
                    poScheduleType: "1",
                    bpCode: bpCode,
                    poNo: artifactId,
                    lastMessageSentMode: lastMessageSentMode,
                    buyerName: buyerName,
                    createdBy: createdBy,
                    supplierName: bpName,
                    recordApprovedStatus: approved === '' ? 'All' : approved === 'Not Approved' ? 0 : approved === 'Approved' ? 1 : approved === 'Rejected' ? 2 : 'All',
                    approved: approved === 'Not Approved' ? 0 : approved === 'Approved' ? 1 : approved === 'Rejected' ? 2 : '',
                    msgStatusFlag: msgStatusFlag === 'Not Sent' ? 0 : msgStatusFlag === 'Pending' ? 1 : msgStatusFlag === 'Successfully Sent' ? 2 : msgStatusFlag === 'Failed to Send' ? 3 : msgStatusFlag === 'Cancelled' ? 4 : '',
                    poStatus: poStatus === 'Complete' ? 'COMPLETE' : poStatus === 'In-Complete' ? 'IN-COMPLETE' : null,
                    dlvyCfmStatus: dlvyCfmStatus,
                    recordStatusFlag: msgStatusFlag === '' ? 'All' : msgStatusFlag === 'Not Sent' ? 0 : msgStatusFlag === 'Pending' ? 1 : msgStatusFlag === 'Successfully Sent' ? 2 : msgStatusFlag === 'Failed to Send' ? 3 : msgStatusFlag === 'Cancelled' ? 4 : 'All',
                    poFromDate: poFromDate,
                    poToDate: poToDate,
                    fromMsgSentTime: fromMsgSentTime,
                    toMsgSentTime: toMsgSentTime,
                    cancellationCount: cancellationCount === 'Yes' ? 1 : cancellationCount === 'No' ? 0 : '',
                    amendmentCount: amendmentCount === 'Yes' ? 1 : amendmentCount === 'No' ? 0 : '',
                    recordCancellationCount: cancellationCount === null ? 'All' : cancellationCount === 'Yes' ? 1 : cancellationCount === 'No' ? 0 : '',
                    recordAmendmentCount: amendmentCount === null ? 'All' : amendmentCount === 'Yes' ? 1 : amendmentCount === 'No' ? 0 : '',
                    fromUpdatedDate: fromUpdatedDate,
                    toUpdatedDate: toUpdatedDate,
                    latestDataSource: latestDataSource,
                    wbsCode: wbsCode,
                    projectName: projectName,
                    deljitFlag: deljitFlag === 'Yes' ? 'X' : deljitFlag === 'No' ? null : deljitFlag === 'No' ? '' :'All',
                    recordDeljitFlg: deljitFlag === null ? 'All' : deljitFlag === 'Yes' ? 'X' :deljitFlag === 'No' ? '':'All',
                    originalPoStatus: originalPoStatus === 'Yet to be received from SAP' ? 0 : originalPoStatus === 'Yet to be sent to supplier' ? 1 : originalPoStatus === 'Sent to Supplier' ? 2 : originalPoStatus === 'Not Applicable' ? 3 : '',
                };

                //filter end 
                paramBody.body = filterBody;
                //default entity filter on no user preferences  filters in db
                if (savedColumnsInResponse.columnsFilter.length <= 0) {
                    paramBody.body.entity.erpEntityCode = defaultEntity;
                    paramBody.body.fromUpdatedDate = isBusinessPartnerPortal ? null : new Date(dateValue.setDate(dateValue.getDate() - 7)).toISOString().slice(0, 10);
                    paramBody.body.poStatus = isBusinessPartnerPortal ? 'IN-COMPLETE' : null;
                    paramBody.body.dlvyCfmStatus = isBusinessPartnerPortal ? 'NOT/PARTIAL CONFIRMED' : null;
                }
                paramBody.sortDir = savedColumnsInResponse.columnsSort.sort;
                paramBody.sortKey = savedColumnsInResponse.columnsSort.field;
                setParamBody({ ...paramBody });

                if (savedColumnsInResponse.columnsFilter.length > 0)
                    setSelectedFilters(savedColumnsInResponse.columnsFilter);
            }
        })
            .catch(function (error) {
                setTableLoadingStatus(false);
                setIntialParamBodyApiCall(true);
                if (error.response !== undefined) {
                    setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response.data.message, severity: "error" });
                }
            });

    }, [selectedLanguage]);

    const approveAndMsgId = tableData.map((row) => {
        return {
            approved: row.approved, msgId: row.msgId, amendmentCount: row.amendmentCount,
            cancellationCount: row.cancellationCount, batchId: row.batchId, poNo: row.poNo,
            poPdfFilePath: row.poPdfFilePath, bpCode: row.bpCode, entityCode: row.entity.erpEntityCode,
            deljitFlag:row.deljitFlag
        }
    })

    const filterValueLoad = approveAndMsgId.filter((item) => {
        if (selectedRow.includes(item.msgId)) {
            return item
        }
    })

    const filterValueApproved = filterValueLoad.map((row) => { return row.approved })
    const amendmentValue = filterValueLoad.map((row) => { return row.amendmentCount })
    const cancellationValue = filterValueLoad.map((row) => { return row.cancellationCount })
    const batchIdValue = filterValueLoad.map((row) => { return row.batchId })
    const filePathValue = filterValueLoad.map((row) => { return row.poPdfFilePath })
    const poNoValue = filterValueLoad.map((row) => { return row.poNo })
    const bpCodeValues = filterValueLoad.map((row) => { return row.bpCode })
    const entittyCodeValue = filterValueLoad.map((row) => { return row.entityCode })
    // const deljitValue = filterValueLoad.map((row) => { return row.deljitFlag})
    const optionForExpPoPRAndAdtHistory = [
        {
            label: intl.formatMessage({ id: "expcfmadthistory.button" }),
            tooltipTitle: intl.formatMessage({ id: "expcfmadthistorybutton.tooltip" }),
            url: '/reports/DLV_SCHEDULE_HISTORY_REPORT',
            successMessage: <FormattedMessage id="snackbar.successexcelReport" defaultMessage="Generated the Excel Report Suceesfully" />,
            parameters: searchedEntityCode === '' && searchedBpCode === '' ?
                isBusinessPartnerPortal ? {
                    erpEntityCode: '', bpCode: '', poScheduleType: "1",
                    dlvyCfmStatus: 'NOT/PARTIAL CONFIRMED',
                    poStatus: 'IN-COMPLETE',
                } : {
                    erpEntityCode: '', bpCode: '', poScheduleType: "1", fromUpdatedDate: new Date(dateValue.setDate(dateValue.getDate() - 0)).toISOString().slice(0, 10),
                } : searchedBpCode === '' ? isBusinessPartnerPortal ? {
                    erpEntityCode: defaultEntity, bpCode: '', poScheduleType: "1",
                    dlvyCfmStatus: 'NOT/PARTIAL CONFIRMED',
                    poStatus: 'IN-COMPLETE',
                } : {
                    erpEntityCode: defaultEntity, bpCode: '', poScheduleType: "1", fromUpdatedDate: new Date(dateValue.setDate(dateValue.getDate() - 0)).toISOString().slice(0, 10),
                } : keyValueDataForDlvSchedule
        }
    ];

    const defaultFilterForReportBpEntEmptyEdi = {
        erpEntityCode: '', bpCode: '',
        poScheduleType: "1",
        fromUpdatedDate: new Date(dateValue.setDate(dateValue.getDate() - 0)).toISOString().slice(0, 10),
        // selectedMsgIds: selectedRow.toString()
    };
    const defaultFilterForReportBpEntEmptyBP = {
        erpEntityCode: '', bpCode: '', poScheduleType: "1",
        dlvyCfmStatus: 'NOT/PARTIAL CONFIRMED',
        poStatus: 'IN-COMPLETE',
        // selectedMsgIds: selectedRow.toString()
    };
    const defaultFilterForReportEntEmptyEdi = {
        erpEntityCode: defaultEntity, bpCode: '', poScheduleType: "1", fromUpdatedDate: new Date(dateValue.setDate(dateValue.getDate() - 0)).toISOString().slice(0, 10),
    };
    const defaultFilterForReportEntEmptyBP = {
        erpEntityCode: defaultEntity, bpCode: '', poScheduleType: "1",
        dlvyCfmStatus: 'NOT/PARTIAL CONFIRMED',
        poStatus: 'IN-COMPLETE',
    };

    const optionForExpPoPrAdtLatest = [
        {
            label: intl.formatMessage({ id: "expdlvcfmlatest.button" }),
            tooltipTitle: intl.formatMessage({ id: "expdlvcfmlatestbutton.tooltip" }),
            url: '/reports/SYS_PO_OUTBOUND_DLV_CFM',
            successMessage: <FormattedMessage id="snackbar.successexcelReport" defaultMessage="Generated the Excel Report Suceesfully" />,
            parameters: selectedRow.length === 0 ?
                searchedEntityCode === '' && searchedBpCode === '' ?
                    !isBusinessPartnerPortal ?
                        defaultFilterForReportBpEntEmptyEdi : defaultFilterForReportBpEntEmptyBP
                    : searchedBpCode === '' ?
                        !isBusinessPartnerPortal ?
                            defaultFilterForReportEntEmptyEdi : defaultFilterForReportEntEmptyBP
                        : keyValueDataForDlvSchedule
                :
                searchedEntityCode === '' && searchedBpCode === '' ?
                    !isBusinessPartnerPortal ?
                        { ...defaultFilterForReportBpEntEmptyEdi, selectedMsgIds: selectedRow.toString() } : { ...defaultFilterForReportBpEntEmptyBP, selectedMsgIds: selectedRow.toString() }
                    : searchedBpCode === '' ?
                        !isBusinessPartnerPortal ?
                            { ...defaultFilterForReportEntEmptyEdi, selectedMsgIds: selectedRow.toString() } : { ...defaultFilterForReportEntEmptyBP, selectedMsgIds: selectedRow.toString() }
                        : { ...keyValueDataForDlvSchedule, selectedMsgIds: selectedRow.toString() }
        }
    ];

    const tempKeyValueData = {
        entity: {
            erpEntityCode: searchedEntityCode === null ? "" : searchedEntityCode,
        },
        bpCode: searchedBpCode === null ? "" : searchedBpCode,
        supplierName: searchedSupplierName,
        approved: searchedApprovedRecordsRecords === 'Not Approved' ? 0 : searchedApprovedRecordsRecords === 'Approved' ? 1 : searchedApprovedRecordsRecords === 'Rejected' ? 2 : null,
        poNo: searchedPoNo,
        createdBy: searchedOrderCreatedBy,
        dlvyCfmStatus: searchedDeliveryConfirmed,
        poStatus: searchedPoStatus === 'Complete' ? 'COMPLETE' : searchedPoStatus === 'In-Complete' ? 'IN-COMPLETE' : null,
        msgStatusFlag: searchedStatusFlag === 'Not Sent' ? 0 : searchedStatusFlag === 'Pending' ? 1 : searchedStatusFlag === 'Successfully Sent' ? 2 : searchedStatusFlag === 'Failed to Send' ? 3 : searchedStatusFlag === 'Cancelled' ? 4 : null,
        lastMessageSentMode: searchedCommType,
        buyerName: searchedBuyerName,
        poFromDate: searchedPoFromDate !== null ? searchedPoFromDate : null,
        poToDate: searchedPoToDate !== null ? searchedPoToDate : null,
        fromLastMessageSentTime: searchedMsgSentFromDate !== null ? searchedMsgSentFromDate : null,
        toLastMessageSentTime: searchedMsgSentToDate !== null ? searchedMsgSentToDate : null,
        poScheduleType: "1",
        fromUpdatedDate: searchedUpdatedFromTime !== null ? searchedUpdatedFromTime : null,
        toUpdatedDate: searchedUpdatedToTime !== null ? searchedUpdatedToTime : null,
        latestDataSource: searchedDataSourceValue,
        cancellationCount: searchedCancelledValue === 'Yes' ? 1 : searchedCancelledValue === 'No' ? 0 : null,
        amendmentCount: searchedAmendedValue === 'Yes' ? 1 : searchedAmendedValue === 'No' ? 0 : null,
        wbsCode: searchedWbsCode,
        projectName: searchedWbsName,
        recordApprovedStatus: searchedApprovedRecordsRecords === '' ? 'All' : searchedApprovedRecordsRecords === 'Not Approved' ? 0 : searchedApprovedRecordsRecords === 'Approved' ? 1 : searchedApprovedRecordsRecords === 'Rejected' ? 2 : 'All',
        recordStatusFlag: searchedStatusFlag === '' ? 'All' : searchedStatusFlag === 'Not Sent' ? 0 : searchedStatusFlag === 'Pending' ? 1 : searchedStatusFlag === 'Successfully Sent' ? 2 : searchedStatusFlag === 'Failed to Send' ? 3 : searchedStatusFlag === 'Cancelled' ? 4 : 'All',
        recordCancellationCount: searchedCancelledValue === null ? 'All' : searchedCancelledValue === 'Yes' ? 1 : searchedCancelledValue === 'No' ? 0 : '',
        recordAmendmentCount: searchedAmendedValue === null ? 'All' : searchedAmendedValue === 'Yes' ? 1 : searchedAmendedValue === 'No' ? 0 : '',
        fromMsgSentTime: searchedMsgSentFromDate !== null ? searchedMsgSentFromDate : null,
        toMsgSentTime: searchedMsgSentToDate !== null ? searchedMsgSentToDate : null,
        deljitFlag: searchedDeljitCount === 'Yes' ? 'X'  : searchedDeljitCount === 'No' ? null :searchedDeljitCount === 'No' ? '' : null,
        recordDeljitFlg: searchedDeljitCount === null ? 'All' : searchedDeljitCount === 'Yes' ? 'X' : searchedDeljitCount === 'No' ? null :searchedDeljitCount === 'No' ? '':'All',
        originalPoStatus: searchedOriginalPoStatus === 'Yet to be received from SAP' ? 0 : searchedOriginalPoStatus === 'Yet to be sent to supplier' ? 1 : searchedOriginalPoStatus === 'Sent to Supplier' ? 2 : searchedOriginalPoStatus === 'Not Applicable' ? 3 : null,
    }

    const optionForExpPoPRAndAdtHistoryForEdi = [
        {
            label: intl.formatMessage({ id: "common.tooltip.breadcrumb.downloadExcelHdr" }),
            tooltipTitle: intl.formatMessage({ id: "common.tooltip.breadcrumb.downloadExcelHdr" }),
            // url: '/reports/SYS_PO_OUTBOUND_HDR',
            url: '/reports/poOutboundMsgs',
            successMessage: <FormattedMessage id="snackbar.successexcelReport" defaultMessage="Generated the Excel Report Suceesfully" />,
            parameters: searchedEntityCode === '' && searchedBpCode === '' ?
                isBusinessPartnerPortal ? {
                    entity: { erpEntityCode: '' }, bpCode: '', poScheduleType: "1",
                    dlvyCfmStatus: 'NOT/PARTIAL CONFIRMED',
                    poStatus: 'IN-COMPLETE',
                } : {
                    entity: { erpEntityCode: '' }, bpCode: '', poScheduleType: "1", fromUpdatedDate: new Date(dateValue.setDate(dateValue.getDate() - 0)).toISOString().slice(0, 10),
                } : searchedBpCode === '' ? isBusinessPartnerPortal ? {
                    entity: { erpEntityCode: defaultEntity }, bpCode: '', poScheduleType: "1",
                    dlvyCfmStatus: 'NOT/PARTIAL CONFIRMED',
                    poStatus: 'IN-COMPLETE',
                } : {
                    entity: { erpEntityCode: defaultEntity }, bpCode: '', poScheduleType: "1", fromUpdatedDate: new Date(dateValue.setDate(dateValue.getDate() - 0)).toISOString().slice(0, 10),
                } : tempKeyValueData
        },
    ];

    const buttonOptionsForDlvCfmQty = [
        {
            label: intl.formatMessage({ id: "uploaddlvschedule.button" }),
            tooltipTitle: intl.formatMessage({ id: "exportbutton.Tooltip" }),
            url: '/reports/poConfirmationDate',
            successMessage: intl.formatMessage({ id: "snackbar.requestedforcfmqtyexcelreport" }),
            parameters: {
                poOutboundMsgHdr: {
                    poNo: searchedPoNo,
                    entity: (searchedEntityCode === '' && searchedBpCode === '') ? '' : searchedEntityCode,
                    bpCode: (searchedEntityCode === '' && searchedBpCode === '') ? '' : searchedBpCode,
                    buyerName: searchedBuyerName,
                    supplierName: searchedSupplierName,
                    createdBy: searchedOrderCreatedBy, poFromDate: searchedPoFromDate, poToDate: searchedPoToDate,
                    updatedFromDate: (searchedEntityCode === '' && searchedBpCode === '') ? isBusinessPartnerPortal ? null : new Date(dateValue.setDate(dateValue.getDate() - 0)).toISOString().slice(0, 10) : (searchedBpCode === '') ? isBusinessPartnerPortal ? null : new Date(dateValue.setDate(dateValue.getDate() - 0)).toISOString().slice(0, 10) : searchedUpdatedFromTime,
                    updatedToDate: searchedUpdatedToTime, poScheduleType: "1",
                    fromMessageSentTime: searchedMsgSentFromDate !== null ? searchedMsgSentFromDate : null,
                    toMessageSentTime: searchedMsgSentToDate !== null ? searchedMsgSentToDate : null,
                    wbsCode: searchedWbsCode, projectName: searchedWbsName, latestDataSource: searchedDataSourceValue, lastMessageSentMode: searchedCommType, approved: searchedApprovedRecordsRecords === 'Not Approved' ? 0 : searchedApprovedRecordsRecords === 'Approved' ? 1 : searchedApprovedRecordsRecords === 'Rejected' ? 2 : null,
                    msgStatusFlag: searchedStatusFlag === 'Not Sent' ? 0 : searchedStatusFlag === 'Pending' ? 1 : searchedStatusFlag === 'Successfully Sent' ? 2 : searchedStatusFlag === 'Failed to Send' ? 3 : searchedStatusFlag === 'Cancelled' ? 4 : null,
                    originalPoStatus: searchedOriginalPoStatus === 'Yet to be received from SAP' ? 0 : searchedOriginalPoStatus === 'Yet to be sent to supplier' ? 1 : searchedOriginalPoStatus === 'Sent to Supplier' ? 2 : searchedOriginalPoStatus === 'Not Applicable' ? 3 : null,
                },
                cancellationCount: searchedCancelledValue === 'Yes' ? 1 : searchedCancelledValue === 'No' ? 0 : '',
                amendmentCount: searchedAmendedValue === 'Yes' ? 1 : searchedAmendedValue === 'No' ? 0 : '',
                dlvyCfmStatus: (searchedEntityCode === '' && searchedBpCode === '') ? isBusinessPartnerPortal ? 'NOT/PARTIAL CONFIRMED' : null :
                    (searchedBpCode === '') ? isBusinessPartnerPortal ? 'NOT/PARTIAL CONFIRMED' : null : searchedDeliveryConfirmed,
                poStatus: (searchedEntityCode === '' && searchedBpCode === '') ? isBusinessPartnerPortal ? 'IN-COMPLETE' : null :
                    (searchedBpCode === '') ? isBusinessPartnerPortal ? 'IN-COMPLETE' : null : searchedPoStatus === 'Complete' ? 'COMPLETE' : searchedPoStatus === 'In-Complete' ? 'IN-COMPLETE' : '',
                recordAmendmentCount: searchedAmendedValue === '' || searchedAmendedValue === null ? 'All' : '',
                recordPoDlvLineCfm: (searchedEntityCode === '' && searchedBpCode === '') ? searchedDeliveryConfirmed === '' || searchedDeliveryConfirmed === null ? 'All' : 'NOT/PARTIAL CONFIRMED' : (searchedBpCode === '') ? searchedDeliveryConfirmed === '' || searchedDeliveryConfirmed === null ? 'All' : 'NOT/PARTIAL CONFIRMED' : searchedDeliveryConfirmed === '' || searchedDeliveryConfirmed === null ? 'All' : searchedDeliveryConfirmed,
                displayBpCode: userAccessBpCode, displayBpName: userAccessBpName,
                recordCancellationCount: searchedCancelledValue === '' || searchedCancelledValue === null ? 'All' : '',
                recordApprovedStatus: searchedApprovedRecordsRecords === '' || searchedApprovedRecordsRecords === null ? 'All' : '',
                recordStatusFlag: searchedStatusFlag === '' || searchedStatusFlag === null ? 'All' : '',
            }
        },
    ];

    const options = isBusinessPartnerPortal ?
        buttonOptionsForDlvCfmQty.concat(optionForExpPoPRAndAdtHistory).concat(optionForExpPoPRAndAdtHistoryForEdi) : optionForExpPoPRAndAdtHistoryForEdi.concat(buttonOptionsForDlvCfmQty).concat(optionForExpPoPRAndAdtHistory).concat(optionForExpPoPrAdtLatest);

    const dlvScheduleButtonForSAP = {
        label: intl.formatMessage({ id: "exportoscfmdatereport.button.tooltip" }),
        tooltipTitle: intl.formatMessage({ id: "exportoscfmdatereport.button.tooltip" }),
        url: '/reports/poConfirmationDateOutSource',
        successMessage: 'Requested excel report for Dlv.Schedule SAP',
        parameters: {}
    };

    const ActionAmendAndCancellation = [
        {
            label: intl.formatMessage({ id: "common.tooltip.breadcrumb.downloadPOPDF" }),
            tooltipTitle: intl.formatMessage({ id: "common.tooltip.breadcrumb.downloadPOPDF" }),
            successMessage: intl.formatMessage({ id: "snackbar.successexcelReport" }),
        },
        {
            label: intl.formatMessage({ id: "common.tooltip.breadcrumb.downloadPOAmend" }),
            tooltipTitle: intl.formatMessage({ id: "common.tooltip.breadcrumb.downloadPOAmend" }),
            url: selectedRow.length > 1 ? `/reports/poOutboundMsgs/download/${selectedRow}` : `/reports/poOutboundMsgs/${selectedRow}`,
            successMessage: intl.formatMessage({ id: "snackbar.successexcelReport" }),
            parameters: {
                reportType: 'revision'
            }
        },
        {
            label: intl.formatMessage({ id: "common.tooltip.breadcrumb.downloadPOcancellation" }),
            tooltipTitle: intl.formatMessage({ id: "common.tooltip.breadcrumb.downloadPOcancellation" }),
            url: selectedRow.length > 1 ? `/reports/poOutboundMsgs/download/${selectedRow}` : `/reports/poOutboundMsgs/${selectedRow}`,
            successMessage: intl.formatMessage({ id: "snackbar.successexcelReport" }),
            parameters: {
                reportType: 'cancellation'
            }
        },
    ];

    const selectPoRevision = [
        {
            label: intl.formatMessage({ id: "selectporevisionletter.text" }),
            tooltipTitle: intl.formatMessage({ id: "selectporevisionletter.text" }),
            successMessage: intl.formatMessage({ id: "snackbar.successexcelReport" }),
        },
    ];

    const selectPoCancellation = [
        {
            label: intl.formatMessage({ id: "selectpocancellationletter.text" }),
            tooltipTitle: intl.formatMessage({ id: "selectpocancellationletter.text" }),
            successMessage: intl.formatMessage({ id: "snackbar.successexcelReport" }),
        },
    ];

    const optionForAction = selectedRevisionList.length > 0 && selectedCancellationList.length > 0 ?
        ActionAmendAndCancellation.concat(selectPoRevision).concat(selectPoCancellation) :
        selectedRevisionList.length > 0 ? ActionAmendAndCancellation.concat(selectPoRevision) :
            selectedCancellationList.length > 0 ? ActionAmendAndCancellation.concat(selectPoCancellation) : ActionAmendAndCancellation;

    const handleMenuItemClick = (event, index) => {
        setSelectedIndex(index);
        requestXLReportExport(options[index], false);
    };

    const handleToggle = (event) => {
        setAnchorEl(null);
        setAnchorE2(anchorRef.current)
    };

    const handleClose = (event) => {
        setAnchorE2(null)
    };

    useEffect(() => {
        setAnchorE2(anchorRef.current);
    }, [anchorRef]);

    function handleClick(event) {
        // requestForSelectedRevisionLetter();
        setAnchorEl(event.currentTarget);
        setAnchorE2(null);
    }

    function handleCloseForAction() {
        // setSelectedRevisionList([]);
        setAnchorEl(null)
    }

    const handleMenuItemClickForAction = (event, index) => {
        setSelectedIndexForAction(index);
        requestPOAmendedCancellation(optionForAction[index], false);
    };

    useEffect(() => {
        if (intialParamBodyApiCall) {
            setTableLoadingStatus(true);
            setTableData([]);
            CommonService.GetListApi(apiUrlAndParamBodyConstants.commonApiUrlForAllTheScreen.poPfOutbound, paramBody)
                .then((response) => {
                    setTableLoadingStatus(false);
                    setTotalRowCount(response.data.totalElements);
                    setTableData(response.data.content);
                    (response.data.totalElements > 0 ?
                        setChipInfoList(response.data.content[0].headerChipList === null ? [] : response.data.content[0].headerChipList)
                        :
                        setChipInfoList([]));

                })
                .catch(function (error) {
                    setTableLoadingStatus(false);

                    if (error.response !== undefined) {
                        setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response.data.message, severity: "error" });
                    }
                });
        }
    }, [paramBody, refreshTable])

    if (columns.length > 0) {

        const entityCodeColumn = columns.find((column) => column.field === columnFieldsName.erpEntityCode);
        const entityCodeColIndex = columns.findIndex((col) => col.field === columnFieldsName.erpEntityCode);

        const entityCodeFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.EntityCodeInputValue,
            }),
        );

        columns[entityCodeColIndex] = {
            ...entityCodeColumn,
            filterOperators: entityCodeFilterOperators,
        };

        const commTypeColumn = columns.find((column) => column.field === columnFieldsName.lastMessageSentMode);
        const commTypeColIndex = columns.findIndex((col) => col.field === columnFieldsName.lastMessageSentMode);

        const commTypeFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.CommTypeLogInputValue,
            }),
        );

        columns[commTypeColIndex] = {
            ...commTypeColumn,
            filterOperators: commTypeFilterOperators,
        };

        const approvedColumn = columns.find((column) => column.field === columnFieldsName.approved);
        const approvedColIndex = columns.findIndex((col) => col.field === columnFieldsName.approved);

        const approvedFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.ApprovedPOInputValue,
            }),
        );

        columns[approvedColIndex] = {
            ...approvedColumn,
            filterOperators: approvedFilterOperators,
        };

        const statusFlagColumn = columns.find((column) => column.field === columnFieldsName.msgStatusFlag);
        const statusFlagColIndex = columns.findIndex((col) => col.field === columnFieldsName.msgStatusFlag);

        const statusFlagFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.PoStatusOutboundColInputValue,
            }),
        );

        columns[statusFlagColIndex] = {
            ...statusFlagColumn,
            filterOperators: statusFlagFilterOperators,
        };

        const poFromDateColumn = columns.find((column) => column.field === columnFieldsName.poFromDate);
        const poFromDateColIndex = columns.findIndex((col) => col.field === columnFieldsName.poFromDate);

        const poFromDateFilterOperators = getGridDateOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.DateInputValue,
            }),
        );

        columns[poFromDateColIndex] = {
            ...poFromDateColumn,
            filterOperators: poFromDateFilterOperators,
        };

        const poToDateColumn = columns.find((column) => column.field === columnFieldsName.poToDate);
        const poToDateColIndex = columns.findIndex((col) => col.field === columnFieldsName.poToDate);

        const poToDateFilterOperators = getGridDateOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.DateInputValue,
            }),
        );

        columns[poToDateColIndex] = {
            ...poToDateColumn,
            filterOperators: poToDateFilterOperators,
        };

        const fromMsgSentTimeColumn = columns.find((column) => column.field === columnFieldsName.fromMsgSentTime);
        const fromMsgSentTimeColIndex = columns.findIndex((col) => col.field === columnFieldsName.fromMsgSentTime);

        const fromMsgSentTimeFilterOperators = getGridDateOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.DateInputValue,
            }),
        );

        columns[fromMsgSentTimeColIndex] = {
            ...fromMsgSentTimeColumn,
            filterOperators: fromMsgSentTimeFilterOperators,
        };

        const toMsgSentTimeColumn = columns.find((column) => column.field === columnFieldsName.toMsgSentTime);
        const toMsgSentTimeColIndex = columns.findIndex((col) => col.field === columnFieldsName.toMsgSentTime);

        const toMsgSentTimeFilterOperators = getGridDateOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.DateInputValue,
            }),
        );

        columns[toMsgSentTimeColIndex] = {
            ...toMsgSentTimeColumn,
            filterOperators: toMsgSentTimeFilterOperators,
        };

        const deliveryCfmStatusColumn = columns.find((column) => column.field === columnFieldsName.dlvyCfmStatus);
        const deliveryCfmStatusColIndex = columns.findIndex((col) => col.field === columnFieldsName.dlvyCfmStatus);

        const deliveryCfmStatusFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.DeliveryCfmStatusInputValue,
            }),
        );

        columns[deliveryCfmStatusColIndex] = {
            ...deliveryCfmStatusColumn,
            filterOperators: deliveryCfmStatusFilterOperators,
        };

        const poStatusColumn = columns.find((column) => column.field === columnFieldsName.poStatus);
        const poStatusColIndex = columns.findIndex((col) => col.field === columnFieldsName.poStatus);

        const poStatusFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.PoStatusOutboundForEDIAndBPColInputValue,
            }),
        );

        columns[poStatusColIndex] = {
            ...poStatusColumn,
            filterOperators: poStatusFilterOperators,
        };

        const amendedColumn = columns.find((column) => column.field === columnFieldsName.amendmentCount);
        const amendedColIndex = columns.findIndex((col) => col.field === columnFieldsName.amendmentCount);

        const amendedFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.PoAmendmentInputValue,
            }),
        );

        columns[amendedColIndex] = {
            ...amendedColumn,
            filterOperators: amendedFilterOperators,
        };

        const cancellationColumn = columns.find((column) => column.field === columnFieldsName.cancellationCount);
        const cancellationColIndex = columns.findIndex((col) => col.field === columnFieldsName.cancellationCount);

        const cancellationFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.PoCancellationInputValue,
            }),
        );

        columns[cancellationColIndex] = {
            ...cancellationColumn,
            filterOperators: cancellationFilterOperators,
        };

        const supplierNameColumn = columns.find((column) => column.field === columnFieldsName.supplierName);
        const supplierNameColIndex = columns.findIndex((col) => col.field === columnFieldsName.supplierName);

        const supplierNameFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.SupplierVendorNameInputValue,
            }),
        );

        columns[supplierNameColIndex] = {
            ...supplierNameColumn,
            filterOperators: supplierNameFilterOperators,
        };

        const fromUpdateTimeColumn = columns.find((column) => column.field === columnFieldsName.fromUpdatedDate);
        const fromUpdateTimeColIndex = columns.findIndex((col) => col.field === columnFieldsName.fromUpdatedDate);

        const fromUpdateTimeFilterOperators = getGridDateOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.DateInputValue,
            }),
        );

        columns[fromUpdateTimeColIndex] = {
            ...fromUpdateTimeColumn,
            filterOperators: fromUpdateTimeFilterOperators,
        };

        const toUpdateTimeColumn = columns.find((column) => column.field === columnFieldsName.toUpdatedDate);
        const toUpdateTimeColIndex = columns.findIndex((col) => col.field === columnFieldsName.toUpdatedDate);

        const toUpdateFilterOperators = getGridDateOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.DateInputValue,
            }),
        );

        columns[toUpdateTimeColIndex] = {
            ...toUpdateTimeColumn,
            filterOperators: toUpdateFilterOperators,
        };

        const wbsCodeColumn = columns.find((column) => column.field === columnFieldsName.wbsCode);
        const wbsCodeColIndex = columns.findIndex((col) => col.field === columnFieldsName.wbsCode);

        const wbsCodeFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.WbsCodeInputValue,
            }),
        );

        columns[wbsCodeColIndex] = {
            ...wbsCodeColumn,
            filterOperators: wbsCodeFilterOperators,
        };

        const wbsNameColumn = columns.find((column) => column.field === columnFieldsName.projectName);
        const wbsNameColIndex = columns.findIndex((col) => col.field === columnFieldsName.projectName);

        const wbsNameFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.WbsDescriptionInputValue,
            }),
        );

        columns[wbsNameColIndex] = {
            ...wbsNameColumn,
            filterOperators: wbsNameFilterOperators,
        };

        const deljitColumn = columns.find((column) => column.field === columnFieldsName.deljitFlag);
        const deljitColIndex = columns.findIndex((col) => col.field === columnFieldsName.deljitFlag);

        const deljitFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.DeljitInputValue,
            }),
        );

        columns[deljitColIndex] = {
            ...deljitColumn,
            filterOperators: deljitFilterOperators,
        };

        const originalPoStatusColumn = columns.find((column) => column.field === columnFieldsName.originalPoStatus);
        const originalPoStatusColIndex = columns.findIndex((col) => col.field === columnFieldsName.originalPoStatus);

        const originalPoStatusFilterOperators = getGridStringOperators().map(
            (operator) => ({
                ...operator,
                InputComponent: CustomFilters.OriginalPoStatusColInputValue,
            }),
        );

        columns[originalPoStatusColIndex] = {
            ...originalPoStatusColumn,
            filterOperators: originalPoStatusFilterOperators,
        };
    }

    useEffect(() => {
        if (totalRowCount === 0) {
            setDisiablingDownloadButtonForZeroRecords(true)
        } else {
            setDisiablingDownloadButtonForZeroRecords(false)
        }
    }, [totalRowCount])


    function currentlySelectedRow(selections) {
        setSelectedRow(selections)
        setAnchorE2(null);
        setAnchorEl(null);
        if (selections.length === 0) {
            isDisableApproveButton(true);
            isDisableRejectionButton(true);
        } else {
            isDisableApproveButton(false);
            isDisableRejectionButton(false);
        }
    }

    useEffect(() => {
        if (selectedRow.length === 0) {
            isDisableApproveButton(true)
            isDisableRejectionButton(true);
        } else if (filterValueApproved.toString().includes('0')) {
            isDisableApproveButton(false);
            isDisableRejectionButton(false);
        } else if (filterValueApproved.toString().includes('1')) {
            isDisableApproveButton(true);
            isDisableRejectionButton(true);
        } else if (filterValueApproved.toString().includes('2')) {
            isDisableApproveButton(false);
            isDisableRejectionButton(true);
        } else {
            isDisableApproveButton(false);
            isDisableRejectionButton(false);
        }
    }, [filterValueApproved])

    const onSubmitApprovals = () => {
        setIsFormSubmitted(true);
        setIsRowSentForApproval(true);
        setOpenBackDrop(true);
        setAnchorEl(null);
        setAnchorE2(null);
        if (filterValueApproved.toString().includes('1')) {
            setAlertBoxState({
                ...alertBoxState, openAlert: true, message: "Selected Records contains already Approved records", severity: "warning"
            });
            setIsRowSentForApproval(false);
            setIsFormSubmitted(false);
            setOpenBackDrop(false);
        } else {
            CommonService.saveOrUpdateOrPost('/poOutboundApprovals', {
                approved: 1,
                msgIds: selectedRow
            })
                .then((response) => {
                    setIsFormSubmitted(false)
                    setIsRowSentForApproval(false)
                    setAlertBoxState({
                        ...alertBoxState, openAlert: true, message: `MessageId : ${selectedRow} has been approved successfully`, severity: "success"
                    });
                    setSelectedRow([])
                    setRefreshTable(!refreshTable);
                    isDisableApproveButton(true);
                    isDisableRejectionButton(true);
                    setOpenBackDrop(false);
                })
                .catch(function (error) {
                    setIsFormSubmitted(false);
                    setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response.message, severity: "error" });
                    isDisableApproveButton(false);
                    setOpenBackDrop(false);
                });
        }
    }

    const onSubmitReject = (data) => {
        setIsFormSubmitted(true);
        setIsRowSentForApproval(true);
        setOpenBackDrop(true);
        setAnchorEl(null);
        setAnchorE2(null);
        if (filterValueApproved.toString().includes('2')) {
            setAlertBoxState({
                ...alertBoxState, openAlert: true, message: "Selected Records contains already Rejected records", severity: "warning"
            });
            setIsRowSentForApproval(false);
            setIsFormSubmitted(false);
            setOpenBackDrop(false);
        } else if (filterValueApproved.toString().includes('1')) {
            setAlertBoxState({
                ...alertBoxState, openAlert: true, message: "Selected Records contains already Approved records", severity: "warning"
            });
            setIsRowSentForApproval(false);
            setIsFormSubmitted(false);
            setOpenBackDrop(false);
        } else {
            CommonService.saveOrUpdateOrPost('/poOutboundApprovals', {
                approved: 2,
                msgIds: selectedRow
            })
                .then((response) => {
                    setIsFormSubmitted(false)
                    setAlertBoxState({
                        ...alertBoxState, openAlert: true, message: `MessageId : ${selectedRow} has been rejected successfully`, severity: "success"
                    });
                    setSelectedRow([])
                    setRefreshTable(!refreshTable);
                    isDisableApproveButton(true);
                    isDisableRejectionButton(true);
                    setOpenBackDrop(false);
                })
                .catch(function (error) {
                    setIsFormSubmitted(false);
                    setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response.message, severity: "error" });
                    isDisableRejectionButton(false);
                    setOpenBackDrop(false);
                });
        }
    }

    const filterableColumns = useMemo(() => GetFilterableColumns(columns), [columns])

    const key = "columnField"

    useEffect(() => {
        let dlvyCfmStatus = null;
        let poStatus = null;
        let poFromDate = null;
        let fromUpdatedDate = null;
        selectedFilters.map((columns) => {
            switch (columns.columnField) {
                case 'dlvyCfmStatus':
                    dlvyCfmStatus = columns.value
                    break;
                case 'poStatus':
                    poStatus = columns.value
                    break;
                case 'fromUpdatedDate':
                    fromUpdatedDate = columns.value
                    break;
                case 'poFromDate':
                    poFromDate = columns.value
                    break;
            }
        })
        setSearchedDeliveryConfirmed(dlvyCfmStatus);
        setSearchedPoFromDate(poFromDate);
        setSearchedPoStatus(poStatus);
        setSearchedUpdatedFromTime(fromUpdatedDate)
    }, [selectedFilters, paramBody])

    const applyFilter = () => {
        let bpName = null;
        let bpCode = null;
        let entityCode = null;
        let buyerName = null;
        let artifactId = null;
        let createdBy = null;
        let msgStatusFlag = null;
        let approved = null;
        let poStatus = null;
        let dlvyCfmStatus = null;
        let lastMessageSentMode = null;
        let amendmentCount = null;
        let cancellationCount = null;
        let poFromDate = null;
        let poToDate = null;
        let fromMsgSentTime = null;
        let toMsgSentTime = null;
        let fromUpdatedDate = null;
        let toUpdatedDate = null;
        let latestDataSource = null;
        let wbsCode = null;
        let projectName = null;
        let deljitFlag = null;
        let originalPoStatus = null;
        selectedFilters.map((columns) => {
            switch (columns.columnField) {
                case 'supplierName':
                    bpName = columns.value
                    break;
                case 'createdBy':
                    createdBy = columns.value
                    break;
                case 'poNo':
                    artifactId = columns.value
                    break;
                case 'lastMessageSentMode':
                    lastMessageSentMode = columns.value
                    break;
                case 'entity.erpEntityCode':
                    entityCode = columns.value
                    break;
                case 'msgStatusFlag':
                    msgStatusFlag = columns.value
                    break;
                case 'bpCode':
                    bpCode = columns.value
                    break;
                case 'approved':
                    approved = columns.value
                    break;
                case 'buyerName':
                    buyerName = columns.value
                    break;
                case 'poFromDate':
                    poFromDate = columns.value
                    break;
                case 'poToDate':
                    poToDate = columns.value
                    break;
                case 'poStatus':
                    poStatus = columns.value
                    break;
                case 'dlvyCfmStatus':
                    dlvyCfmStatus = columns.value
                    break;
                case 'cancellationCount':
                    cancellationCount = columns.value
                    break;
                case 'amendmentCount':
                    amendmentCount = columns.value
                    break;
                case 'fromMsgSentTime':
                    fromMsgSentTime = columns.value
                    break;
                case 'toMsgSentTime':
                    toMsgSentTime = columns.value
                    break;
                case 'fromUpdatedDate':
                    fromUpdatedDate = columns.value
                    break;
                case 'toUpdatedDate':
                    toUpdatedDate = columns.value
                    break;
                case 'latestDataSource':
                    latestDataSource = columns.value
                    break;
                case 'wbsCode':
                    wbsCode = columns.value
                    break;
                case 'projectName':
                    projectName = columns.value
                    break;
                case 'deljitFlag':
                    deljitFlag = columns.value
                    break;
                case 'originalPoStatus':
                    originalPoStatus = columns.value
                    break;
            }
        })

        setSearchedEntityCode(entityCode);
        setSearchedPoNo(artifactId);
        setSearchedBpCode(bpCode);
        setSearchedApprovedRecordsRecords(approved);
        setSearchedCommType(lastMessageSentMode);
        setSearchedStatusFlag(msgStatusFlag);
        setSearchedBuyerName(buyerName);
        setSearchedSupplierName(bpName);
        setSearchedPoFromDate(poFromDate);
        setSearchedPoToDate(poToDate);
        setSeachedMessageSentFromDate(fromMsgSentTime);
        setSeachedMessageSentToDate(toMsgSentTime);
        setSearchedPoStatus(poStatus);
        setSearchedDeliveryConfirmed(dlvyCfmStatus);
        setSearchedOrderCreatedBy(createdBy);
        setSearchedAmendedValue(amendmentCount);
        setSearchedCancelledValue(cancellationCount);
        setSearchedUpdatedFromTime(fromUpdatedDate)
        setSearchedUpdatedToTime(toUpdatedDate);
        setSearchedDataSourceValue(latestDataSource);
        setSearchedWbsCode(wbsCode);
        setSearchedWbsName(projectName);
        setSearchedDeljitValue(deljitFlag);
        setSearchedOriginalPoStatus(originalPoStatus);

        savePreference("filter", selectedFilters.length === 0 ? defaulSelectedFilters : selectedFilters);
        setParamBody(prevState => {
            return {
                ...prevState, body: {
                    entity: {
                        erpEntityCode: entityCode,
                    },
                    poScheduleType: "1",
                    bpCode: bpCode,
                    poNo: artifactId,
                    lastMessageSentMode: lastMessageSentMode,
                    buyerName: buyerName,
                    createdBy: createdBy,
                    supplierName: bpName,
                    recordApprovedStatus: approved === '' ? 'All' : approved === 'Not Approved' ? 0 : approved === 'Approved' ? 1 : approved === 'Rejected' ? 2 : 'All',
                    approved: approved === 'Not Approved' ? 0 : approved === 'Approved' ? 1 : approved === 'Rejected' ? 2 : '',
                    msgStatusFlag: msgStatusFlag === 'Not Sent' ? 0 : msgStatusFlag === 'Pending' ? 1 : msgStatusFlag === 'Successfully Sent' ? 2 : msgStatusFlag === 'Failed to Send' ? 3 : msgStatusFlag === 'Cancelled' ? 4 : '',
                    poStatus: poStatus === 'Complete' ? 'COMPLETE' : poStatus === 'In-Complete' ? 'IN-COMPLETE' : null,
                    dlvyCfmStatus: dlvyCfmStatus,
                    recordStatusFlag: msgStatusFlag === '' ? 'All' : msgStatusFlag === 'Not Sent' ? 0 : msgStatusFlag === 'Pending' ? 1 : msgStatusFlag === 'Successfully Sent' ? 2 : msgStatusFlag === 'Failed to Send' ? 3 : msgStatusFlag === 'Cancelled' ? 4 : 'All',
                    poFromDate: poFromDate,
                    poToDate: poToDate,
                    fromMsgSentTime: fromMsgSentTime,
                    toMsgSentTime: toMsgSentTime,
                    cancellationCount: cancellationCount === 'Yes' ? 1 : cancellationCount === 'No' ? 0 : '',
                    amendmentCount: amendmentCount === 'Yes' ? 1 : amendmentCount === 'No' ? 0 : '',
                    recordCancellationCount: cancellationCount === null ? 'All' : cancellationCount === 'Yes' ? 1 : cancellationCount === 'No' ? 0 : '',
                    recordAmendmentCount: amendmentCount === null ? 'All' : amendmentCount === 'Yes' ? 1 : amendmentCount === 'No' ? 0 : '',
                    fromUpdatedDate: fromUpdatedDate,
                    toUpdatedDate: toUpdatedDate,
                    latestDataSource: latestDataSource,
                    wbsCode: wbsCode,
                    projectName: projectName,
                    deljitFlag: deljitFlag === 'Yes' ? 'X' :  deljitFlag === 'No' ? null: deljitFlag === 'No' ? '':'All',
                    recordDeljitFlg: deljitFlag === null ? 'All' : deljitFlag === 'Yes' ? 'X'  :deljitFlag === 'No' ? '':'All',
                    originalPoStatus: originalPoStatus === 'Yet to be received from SAP' ? 0 : originalPoStatus === 'Yet to be sent to supplier' ? 1 : originalPoStatus === 'Sent to Supplier' ? 2 : originalPoStatus === 'Not Applicable' ? 3 : '',

                }
            }
        })
    }

    useEffect(() => {
        if (intialParamBodyApiCall)
            setFilterCount([...new Map(selectedFilters.map(item => [item[key], item])).values()].length)
    }, [selectedFilters, tableData])

    let filterBodyObjForDlvSchedule = {
        erpEntityCode: searchedEntityCode === null ? "" : searchedEntityCode,
        bpCode: searchedBpCode === null ? "" : searchedBpCode,
        supplierName: searchedSupplierName,
        poNo: searchedPoNo,
        poScheduleType: "1",
        poFromDate: searchedPoFromDate !== null ? searchedPoFromDate : null,
        poToDate: searchedPoToDate !== null ? searchedPoToDate : null,
        approved: searchedApprovedRecordsRecords === 'Not Approved' ? 0 : searchedApprovedRecordsRecords === 'Approved' ? 1 : searchedApprovedRecordsRecords === 'Rejected' ? 2 : null,
        buyerName: searchedBuyerName,
        createdBy: searchedOrderCreatedBy,
        fromUpdatedDate: searchedUpdatedFromTime !== null ? searchedUpdatedFromTime : null,
        toUpdatedDate: searchedUpdatedToTime !== null ? searchedUpdatedToTime : null,
        dlvyCfmStatus: searchedDeliveryConfirmed,
        lastMessageSentMode: searchedCommType,
        latestDataSource: searchedDataSourceValue,
        fromLastMessageSentTime: searchedMsgSentFromDate !== null ? searchedMsgSentFromDate : null,
        toLastMessageSentTime: searchedMsgSentToDate !== null ? searchedMsgSentToDate : null,
        cancellationCount: searchedCancelledValue === 'Yes' ? 1 : searchedCancelledValue === 'No' ? 0 : null,
        amendmentCount: searchedAmendedValue === 'Yes' ? 1 : searchedAmendedValue === 'No' ? 0 : null,
        poStatus: searchedPoStatus === 'Complete' ? 'COMPLETE' : searchedPoStatus === 'In-Complete' ? 'IN-COMPLETE' : null,
        msgStatusFlag: searchedStatusFlag === 'Not Sent' ? 0 : searchedStatusFlag === 'Pending' ? 1 : searchedStatusFlag === 'Successfully Sent' ? 2 : searchedStatusFlag === 'Failed to Send' ? 3 : searchedStatusFlag === 'Cancelled' ? 4 : null,
        wbsCode: searchedWbsCode,
        projectName: searchedWbsName,
        originalPoStatus: searchedOriginalPoStatus === 'Yet to be received from SAP' ? 0 : searchedOriginalPoStatus === 'Yet to be sent to supplier' ? 1 : searchedOriginalPoStatus === 'Sent to Supplier' ? 2 : searchedOriginalPoStatus === 'Not Applicable' ? 3 :  null,
    }

    useEffect(() => {
        let colValDataObj = {}
        for (let key in filterBodyObjForDlvSchedule) {
            let value = filterBodyObjForDlvSchedule[key]
            if (filterBodyObjForDlvSchedule.hasOwnProperty(key) && ((value !== null) && (value !== undefined) && (value !== {}))) {
                colValDataObj[key] = value.toString();
            }
            setKeyValueDataForDlvSchedule(colValDataObj)
        }
    }, [searchedEntityCode, searchedBpCode, searchedSupplierName, searchedPoNo, searchedDeliveryConfirmed, searchedPoStatus, searchedStatusFlag, searchedCommType, searchedBuyerName, searchedApprovedRecordsRecords, searchedPoFromDate, searchedPoToDate, searchedMsgSentFromDate, searchedMsgSentToDate, searchedOrderCreatedBy, searchedUpdatedFromTime, searchedUpdatedToTime, searchedWbsCode, searchedWbsName]);

    useEffect(() => {
        if (selectedRow.length === 1) {
            CommonService.GetListApi(`/poOutboundAuditViewCount/${selectedRow}/batchId/${batchIdValue}`, {})
                .then((response) => {
                    setAuditViewAmendmentValue(response.data)
                })
        }
    }, [selectedRow,])

    const requestPOAmendedCancellation = (selectedExportReportTypeObj) => {
        setOpenBackDrop(true);
        //  if (selectedRow.length > 1) {
        //     setAlertBoxState({
        //         ...alertBoxState, openAlert: true, message: "Please Select only one PO", severity: "warning"
        //   });
        //   setOpenBackDrop(false);
        //} else
        if (selectedRow.length === 0) {
            setOpenBackDrop(false);
        } else if (selectedExportReportTypeObj.label === intl.formatMessage({ id: "selectporevisionletter.text" })) {
            setOpenBackDrop(false);
        } else if (selectedExportReportTypeObj.label === intl.formatMessage({ id: "selectpocancellationletter.text" })) {
            setOpenBackDrop(false);
        } else if (selectedExportReportTypeObj.label === intl.formatMessage({ id: "common.tooltip.breadcrumb.downloadPOPDF" }) && selectedRow.length == 1) {
            if (filePathValue.toString() === null || filePathValue.toString() === '' || filePathValue.toString() === undefined) {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: `Selected PO number does not contain PDF file`, severity: "error" });
                setOpenBackDrop(false);
            } else {
                downloadPoPdf(filePathValue.toString());
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: selectedExportReportTypeObj.successMessage, severity: "success" });
                setOpenBackDrop(false);
            }
            setOpenBackDrop(false);
        } else if (selectedExportReportTypeObj.label === intl.formatMessage({ id: "common.tooltip.breadcrumb.downloadPOPDF" }) && selectedRow.length > 1) {
            let count = 0;
            let poNos = "";
            for (let j = 0; j < filePathValue.length; j++) {
                if (filePathValue[j] === null || filePathValue.toString() === '' || filePathValue.toString() === undefined) {
                    count = count + 1;
                    for (let k = 0; k < poNoValue.length; k++) {
                        if (j == k) {
                            poNos = poNos + poNoValue[k].concat(',');
                        }

                    }
                }
            }
            if (count >= 1) {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: `Selected  PO numbers ${poNos.slice(0, -1)} from multiple selection does not contain PDF file`, severity: "error" });
                setOpenBackDrop(false);
            } else {
                if (selectedRow.length > 1) {
                    downloadPoPdfZipFile(filePathValue.toString());

                } else {
                    downloadPoPdf(filePathValue.toString());
                }
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: selectedExportReportTypeObj.successMessage, severity: "success" });
                setOpenBackDrop(false);
            }
            setOpenBackDrop(false);
        } else {
            CommonService.GetListApi(selectedExportReportTypeObj.url, selectedExportReportTypeObj.parameters)
                .then((response) => {
                    if (selectedRow.length > 1) {
                        setAlertBoxState({ ...alertBoxState, openAlert: true, message: <FormattedMessage id="yourrequestno.pdf.snackbar.text" values={{ responsedata: `${response.data.value}`, reportName: `${response.data.reportName}` }} />, severity: "success" });

                    } else {
                        showDownloadPopUpForAmendedCancellation(response.data.fileUrl);
                        setAlertBoxState({ ...alertBoxState, openAlert: true, message: selectedExportReportTypeObj.successMessage, severity: "success" });
                    }
                    setOpenBackDrop(false);
                })
                .catch(function (error) {
                    setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response === undefined ? 'Error occured' : error.response.message, severity: "error" });
                    setOpenBackDrop(false);
                });
        }
    }

    useEffect(() => {
        if (selectedRow.length === 1) {
            CommonService.GetListApi(`/poOutboundMsgs/amendedSeqs/${selectedRow}`, {})
                .then((response) => {
                    setSelectedRevisionList(response.data);
                })
                .catch(function (error) {
                    setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response === undefined ? 'Error occured' : error.response.message, severity: "error" });
                });
        }
    }, [selectedRow])

    useEffect(() => {
        if (selectedRow.length === 1) {
            CommonService.GetListApi(`/poOutboundMsgs/cancellationSeqs/${selectedRow}`, {})
                .then((response) => {
                    setSelectedCancellationList(response.data);
                })
                .catch(function (error) {
                    setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response === undefined ? 'Error occured' : error.response.message, severity: "error" });
                });
        }
    }, [selectedRow])

    const showDownloadPopUpForAmendedCancellation = (filePath) => {
        const filePathReplaceWithDoubleBackwardSlash = filePath.replaceAll('\\', '/');
        const fileName = filePathReplaceWithDoubleBackwardSlash.split('/')[filePathReplaceWithDoubleBackwardSlash.split('/').length - 1].trim();
        fetch(`${process.env.REACT_APP_API_URL}/downloadFileFromServer?filePath=${filePathReplaceWithDoubleBackwardSlash}`, {
            method: 'GET',
            headers: {
                authorization: 'Bearer ' + CommonService.getJwtTokenOfLoggedInUser()
            },
        })
            .then((response) => {
                return response.blob();
            }
            )
            .then((blob) => {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: <FormattedMessage id="successmessage.dowload" defaultMessage="File downloaded successfully" />, severity: "success" });
                saveAs(blob, fileName);
                var url = URL.createObjectURL(blob);
                window.open(url, fileName)
            })
            .catch(error => {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: <FormattedMessage id="errorMessage.download" defaultMessage="Failed to download the file!" />, severity: "error" });
            })
    }

    const requestXLReportExport = (selectedExportReportTypeObj, isExportDlvScheduleForSap) => {
        isExportDlvScheduleForSap ? setDlvScheduleForSapExportXlRequested(true) : setExportXlRequested(true);
        setExportXlRequested(true);
        setOpenBackDrop(true);
        if ((selectedExportReportTypeObj.label === intl.formatMessage({ id: "uploaddlvschedule.button" }) && (searchedDeliveryConfirmed === null && (searchedPoStatus === null) && searchedPoFromDate === null && searchedPoNo === null))) {
            setAlertBoxState({
                ...alertBoxState, openAlert: true, message: "Kindly input atleast one filter (PO from Date or Dlv Cfm Status or PO Dlv Status or PO No) to download DLV CFM SCHEDULE report", severity: "warning"
            });
            setOpenBackDrop(false);
            setExportXlRequested(false);
        } else if (selectedExportReportTypeObj.label === intl.formatMessage({ id: "uploaddlvschedule.button" }) && searchedPoStatus === 'Complete') {
            setAlertBoxState({
                ...alertBoxState, openAlert: true, message: "Kindly filter for In-Complete PO, Cannot Insert Confirmation Date and Qty for PO which is completed.", severity: "warning"
            });
            setOpenBackDrop(false);
            setExportXlRequested(false);
        } else if (selectedExportReportTypeObj.label === intl.formatMessage({ id: "uploaddlvschedule.button" }) && searchedCancelledValue === 'Yes') {
            setAlertBoxState({
                ...alertBoxState, openAlert: true, message: "Kindly filter for Not Cancelled PO, Cannot Insert Confirmation Date and Qty for PO which is Cancelled.", severity: "warning"
            });
            setOpenBackDrop(false);
            setExportXlRequested(false);
        } else if (selectedExportReportTypeObj.label === intl.formatMessage({ id: "uploaddlvschedule.button" })) {
            CommonService.saveOrUpdateOrPost(selectedExportReportTypeObj.url, selectedExportReportTypeObj.parameters)
                .then((response) => {
                    setExportXlRequested(false);
                    setDlvScheduleForSapExportXlRequested(false);
                    setAlertBoxState({ ...alertBoxState, openAlert: true, message: <FormattedMessage id="yourrequestno.snackbar.text" values={{ responsedata: `${response.data.value}`, reportName: `${response.data.reportName}` }} />, severity: "success" });
                    setOpenBackDrop(false);
                })
                .catch(function (error) {
                    setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response === undefined ? 'Error occured' : error.response.message, severity: "error" });
                    setOpenBackDrop(false);
                    setExportXlRequested(false);
                    setDlvScheduleForSapExportXlRequested(false);
                });
        } else {
            CommonService.saveOrUpdateOrPost(selectedExportReportTypeObj.url, selectedExportReportTypeObj.parameters)
                .then((response) => {
                    setExportXlRequested(false);
                    setDlvScheduleForSapExportXlRequested(false);
                    if (response.data.sucess === "No records found for given condition")
                        setAlertBoxState({ ...alertBoxState, openAlert: true, message: response.data.sucess, severity: "error" });
                    else {
                        //showDownloadPopUp(response.data.fileUrl);
                        setAlertBoxState({ ...alertBoxState, openAlert: true, message: <FormattedMessage id="yourrequestno.snackbar.text" values={{ responsedata: `${response.data.value}`, reportName: `${response.data.reportName}` }} />, severity: "success" });
                    }
                    setOpenBackDrop(false);
                })
                .catch(function (error) {
                    setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response === undefined ? 'Error occured' : error.response.message, severity: "error" });
                    setOpenBackDrop(false);
                    setExportXlRequested(false);
                    setDlvScheduleForSapExportXlRequested(false);
                });
        }
    }

    const requestSelectedPoRevision = () => {
        setOpenBackDrop(true);
        CommonService.GetListApi(`/reports/poOutboundMsgs/${selectedRow}`, {
            reportType: 'revision',
            batchId: selectedRevisionBatchId,
            msgId: selectedRevisionMsgId
        })
            .then((response) => {
                showDownloadPopUpForPORevisonSelected(response.data.fileUrl);
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: intl.formatMessage({ id: "snackbar.successexcelReport" }), severity: "success" });
                setOpenBackDrop(false);
            })
            .catch(function (error) {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response === undefined ? 'Error occured' : error.response.message, severity: "error" });
                setOpenBackDrop(false);
            });
    }

    const requestSelectedPoCancellation = () => {
        setOpenBackDrop(true);
        CommonService.GetListApi(`/reports/poOutboundMsgs/${selectedRow}`, {
            reportType: 'CANCELLATION',
            batchId: selectedCancellationBatchId,
            msgId: selectedCancellationMsgId
        })
            .then((response) => {
                showDownloadPopUpForPORevisonSelected(response.data.fileUrl);
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: intl.formatMessage({ id: "snackbar.successexcelReport" }), severity: "success" });
                setOpenBackDrop(false);
            })
            .catch(function (error) {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: error.response === undefined ? 'Error occured' : error.response.message, severity: "error" });
                setOpenBackDrop(false);
            });
    }

    useEffect(() => {
        if (selectedRevisionBatchId !== '' && selectedRevisionMsgId !== '') {
            requestSelectedPoRevision();
        }
    }, [selectedRevisionBatchId, selectedRevisionMsgId]);

    useEffect(() => {
        if (selectedCancellationBatchId !== '' && selectedCancellationMsgId !== '') {
            requestSelectedPoCancellation();
        }
    }, [selectedCancellationBatchId, selectedCancellationMsgId]);

    const showDownloadPopUpForPORevisonSelected = (filePath) => {
        const filePathReplaceWithDoubleBackwardSlash = filePath.replaceAll('\\', '/');
        const fileName = filePathReplaceWithDoubleBackwardSlash.split('/')[filePathReplaceWithDoubleBackwardSlash.split('/').length - 1].trim();
        fetch(`${process.env.REACT_APP_API_URL}/downloadFileFromServer?filePath=${filePathReplaceWithDoubleBackwardSlash}`, {
            method: 'GET',
            headers: {
                authorization: 'Bearer ' + CommonService.getJwtTokenOfLoggedInUser()
            },
        })
            .then((response) => {
                return response.blob();
            }
            )
            .then((blob) => {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: <FormattedMessage id="successmessage.dowload" defaultMessage="File downloaded successfully" />, severity: "success" });
                saveAs(blob, fileName);
                var url = URL.createObjectURL(blob);
                window.open(url, fileName)
            })
            .catch(error => {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: <FormattedMessage id="errorMessage.download" defaultMessage="Failed to download the file!" />, severity: "error" });
            })
    }

    const downloadPoPdf = (filePath) => {
        const filePathReplaceWithDoubleBackwardSlash = filePath.replaceAll('\\', '/');
        const fileName = filePathReplaceWithDoubleBackwardSlash.split('/')[filePathReplaceWithDoubleBackwardSlash.split('/').length - 1].trim();
        fetch(`${process.env.REACT_APP_API_URL}/downloadFileFromServer?filePath=${filePathReplaceWithDoubleBackwardSlash}`, {
            method: 'GET',
            headers: {
                authorization: 'Bearer ' + CommonService.getJwtTokenOfLoggedInUser()
            },
        })
            .then((response) => {
                return response.blob();
            }
            )
            .then((blob) => {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: <FormattedMessage id="successmessage.dowload" defaultMessage="File downloaded successfully" />, severity: "success" });
                saveAs(blob, fileName);
                var url = URL.createObjectURL(blob);
                window.open(url, fileName)
            })
            .catch(error => {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: <FormattedMessage id="errorMessage.download" defaultMessage="Failed to download the file!" />, severity: "error" });
            })
    }

    const downloadPoPdfZipFile = (filePath) => {
        const filePathReplaceWithDoubleBackwardSlash = filePath.replaceAll('\\', '/');
        // const fileName = filePathReplaceWithDoubleBackwardSlash.split('/')[filePathReplaceWithDoubleBackwardSlash.split('/').length - 1].trim();
        var fileName = filePathReplaceWithDoubleBackwardSlash.split('/')[filePathReplaceWithDoubleBackwardSlash.split('/').length - 1].trim();
        const current = new Date();
        const date = `${current.getFullYear()}-${current.getMonth() + 1}-${current.getDate()}_${current.getHours()}_${current.getMinutes()}_${current.getSeconds()}`;
        fileName = "PO_PDF_" + date;
        fetch(`${process.env.REACT_APP_API_URL}/zip-download?MultiplefileName=${filePathReplaceWithDoubleBackwardSlash}`, {
            method: 'GET',
            headers: {
                authorization: 'Bearer ' + CommonService.getJwtTokenOfLoggedInUser()
            },
        })
            .then((response) => {
                return response.blob();
            }
            )
            .then((blob) => {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: <FormattedMessage id="successmessage.dowload" defaultMessage="File downloaded successfully" />, severity: "success" });
                saveAs(blob, fileName);
                // var url = URL.createObjectURL(blob);
                // window.open(url)
            })
            .catch(error => {
                setAlertBoxState({ ...alertBoxState, openAlert: true, message: <FormattedMessage id="errorMessage.download" defaultMessage="Failed to download the file!" />, severity: "error" });
            })
    }
    return (
        <React.Fragment>
            <CssBaseline />
            <Snackbar
                anchorOrigin={{ vertical, horizontal }}
                open={openAlert}
                onClose={handleCloseAlert}
                key={vertical + horizontal}
                autoHideDuration={9000}
            >
                <Alert onClose={handleCloseAlert} severity={severity}>
                    {message}
                </Alert>
            </Snackbar>
            <CommonBackDrop open={openBackDrop} />
            <Grid container className={classes.rootForEditPurchaseOrder}>
                <Grid item container justifyContent='flex-start' alignItems='center' xs={12} sm={12} md={12} lg={12} className={classes.topGrid} style={{ marginLeft: '-0.5%' }}>
                    <CustomBreadCrumb componentList={componentList} />
                </Grid>
                {!isBusinessPartnerPortal && <ChipInfoList chipInfoList={chipInfoList} />}
                <CustomFilterComponent open={openFilterPanel} onClose={handleCloseForFilter} filterableColumns={filterableColumns}
                    setSelectedFilters={setSelectedFilters} selectedFilters={selectedFilters} applyFilter={applyFilter}
                    defaulSelectedFilters={defaulSelectedFilters} />
                <FileUploadDialog open={openFileUploadDialog} onClose={closeFileUploadDialog} isBusinessPartnerPortal={isBusinessPartnerPortal} defaultEntity={defaultEntity}
                    defaultBpType={"SUPPLIER"} />
                <CommonReportDownloadForMsg open={openReportDownloadDialog} bpCode={bpCodeValues} entityCode={entittyCodeValue} proccessValue={{ processId: 11, processName: 'Purchase Order' }}
                    onClose={closeReportDownloadDialog} msgIdvalue={selectedRow} />
                <DeliveryConfirmationStatistics open={openDlvyCfmStatDialog} onClose={closeDlvyCfmStatDialog} />
                {/* <Grid container className={classes.rootForEditPurchaseOrder}> */}
                <Paper elevation={3} className={isBusinessPartnerPortal ? classes.gridHeightAndWidthForBP : classes.gridHeightAndWidth}>
                    <div style={{ height: '100%', width: '100%' }}>
                        <DataGridPro
                            className={classes.customTableStyle}
                            sortingMode="server"
                            rows={tableData}
                            columns={columns}
                            showToolbar
                            getRowId={(row) => row.msgId}
                            pageSize={paramBody.recordsPerPage}
                            loading={tableLoadingStatus}
                            rowCount={totalRowCount}
                            scrollbarSize={30}
                            disableColumnFilter={true}
                            rowsPerPageOptions={[1, 2, 10, 25, 50, 100]}
                            pagination
                            density="compact"
                            paginationMode="server"
                            disableSelectionOnClick={true}
                            disableMultipleColumnsSorting={true}
                            hideFooter={true}
                            checkboxSelection={true}
                            onSelectionModelChange={currentlySelectedRow}
                            selectionModel={selectedRow}
                            isRowSelectable={(params) => true}
                            localeText={localtext}
                            components={{
                                Toolbar: () => {
                                    return (
                                        <Grid container direction="row" justifyContent="flex-start" alignItems="center"
                                            style={mobileScreen ? { marginBottom: '-0.1%', marginTop: '0%' } : { marginBottom: '-0.3%', marginTop: '-0.5%' }}>
                                            <Grid item container direction="row" justifyContent="flex-start" xs={12} sm={12} md={8} lg={8}>
                                                <Grid>
                                                    <Tooltip title={<FormattedMessage id="common.columntoolbar.tooltip" />} placement='bottom'>
                                                        <GridToolbarColumnsButton className={classes.toobarStyle} variant="outlined" size="medium" />
                                                    </Tooltip>
                                                </Grid>
                                                <Grid>
                                                    <Tooltip title={<FormattedMessage id="common.density.tooltip" />} placement='bottom'>
                                                        <GridToolbarDensitySelector className={classes.toobarStyle} variant="outlined" size="medium" />
                                                    </Tooltip>
                                                </Grid>
                                                <Grid>
                                                    <Tooltip title={<FormattedMessage id="common.toolbarfilterstooltipshow.toolbar.lable" />} placement='bottom'>
                                                        <span>
                                                            <Button className={classes.refreshToobarStyle} variant="outlined" size="medium"
                                                                onClick={() => { setFilterPanel(true); setAnchorEl(null); setAnchorE2(null) }}
                                                            >
                                                                <Badge color="primary" badgeContent={filterCount}>
                                                                    <FilterList />
                                                                </Badge>
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid>
                                                    {(poPfOutboundModuleAccess !== undefined ? poPfOutboundModuleAccess.createFlag || poPfOutboundModuleAccess.editFlag : false) && (!isBusinessPartnerPortal) && (<Tooltip title={<FormattedMessage id="common.button.approveselectedrecords.tooltip" defaultMessage="Approve selected records" />} placement='bottom'>
                                                        <span>
                                                            <Button size="small" color="primary" variant="outlined"
                                                                className={classes.refreshToobarStyle}
                                                                onClick={onSubmitApprovals}
                                                                disabled={disableApproveButton || isFormSubmitted}
                                                            >
                                                                <IconApproval size={20} />
                                                            </Button>
                                                        </span>
                                                    </Tooltip>)}
                                                </Grid>
                                                <Grid>
                                                    {(poPfOutboundModuleAccess !== undefined ? poPfOutboundModuleAccess.createFlag || poPfOutboundModuleAccess.editFlag : false) && (!isBusinessPartnerPortal) && (
                                                        <Tooltip title={<FormattedMessage id="common.button.rejectselectedrecords.tooltip" defaultMessage="Reject selected records" />} placement='bottom'>
                                                            <span>
                                                                <Button size="small" color="primary" variant="outlined"
                                                                    className={classes.refreshToobarStyle}
                                                                    onClick={onSubmitReject}
                                                                    disabled={disableRejectionButton || isFormSubmitted}
                                                                >
                                                                    <CancelIcon size={20} />
                                                                </Button>
                                                            </span>
                                                        </Tooltip>)}
                                                </Grid>
                                                <Grid>
                                                    <Tooltip title={options[selectedIndex].tooltipTitle} placement='bottom'>
                                                        <span>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                ref={anchorRef}
                                                                color="primary"
                                                                disabled={tableData.length <= 0 || isExportXlRequested}
                                                                endIcon={isExportXlRequested ? <CircularProgress color="secondary" size={20} /> : null}
                                                                onClick={handleToggle}
                                                                style={{ marginRight: '5px' }}
                                                            >
                                                                <ExportIcon />
                                                                <Typography color="primary" style={{ margin: '0 5px 0 5px', fontWeight: 500, fontSize: '13px' }} >
                                                                    <FormattedMessage id="export.defaulttext.button" />
                                                                </Typography>
                                                                <ArrowDropDownIcon />
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                    <Menu
                                                        id="selectReportDownload"
                                                        anchorPosition={isBusinessPartnerPortal ? { left: 150, top: 125 } : { left: 240, top: 150 }}
                                                        anchorReference="anchorPosition"
                                                        open={Boolean(anchorE2)}
                                                        elevation={5}
                                                        onClick={handleClose}
                                                    >
                                                        <MenuList>
                                                            {options.map((option, index) => (
                                                                <Tooltip title={options[index].tooltipTitle} placement="right-start">
                                                                    <span>
                                                                        <MenuItem
                                                                            divider
                                                                            className={classes.menuListStyle}
                                                                            key={index}
                                                                            selected={index === selectedIndex}
                                                                            onClick={(event) => handleMenuItemClick(event, index)}
                                                                        >
                                                                            {option.label}
                                                                        </MenuItem>
                                                                    </span>
                                                                </Tooltip>
                                                            ))}
                                                        </MenuList>
                                                    </Menu>
                                                </Grid>
                                                {<Grid>
                                                    <Button
                                                        disabled={selectedRow.length === 0 ? true : false}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        ref={anchorRef}
                                                        style={{ marginRight: '5px' }}
                                                        onClick={handleClick}>
                                                        <Typography color="primary" style={{ margin: '0 5px 0 5px', fontWeight: 500, fontSize: '13px' }} >
                                                            {intl.formatMessage({ id: "toolbar.downloadpdf.text" })}
                                                        </Typography>
                                                        <ArrowDropDownIcon />
                                                    </Button>
                                                    <Menu
                                                        id="selectRevCancellationLetter"
                                                        anchorPosition={isBusinessPartnerPortal ? { left: 290, top: 125 } : { left: 400, top: 155 }}
                                                        anchorReference="anchorPosition"
                                                        keepMounted
                                                        open={Boolean(anchorEl)}
                                                        elevation={5}
                                                        onClick={handleCloseForAction}
                                                    >
                                                        <MenuList>
                                                            {optionForAction.map((option, index) => (
                                                                <Tooltip title={optionForAction[index].tooltipTitle} placement="right-start">
                                                                    <span>
                                                                        <MenuItem
                                                                            divider
                                                                            className={classes.menuListStyle}
                                                                            key={index}
                                                                            disabled={((amendmentValue.includes(0) && auditViewAmendmentValue === 0) ||
                                                                                (auditViewAmendmentValue === 0) || (amendmentValue.includes(0))) && index === 1 ||
                                                                                cancellationValue.includes(0) && index === 2}
                                                                            selected={index === selectedIndexForAction}
                                                                            onClick={(event) => {
                                                                                handleMenuItemClickForAction(event, index)
                                                                            }}
                                                                        >
                                                                            {option.label}

                                                                            {(option.label === intl.formatMessage({ id: "selectporevisionletter.text" })) &&
                                                                                <Button size="small"
                                                                                    style={{ maxWidth: '20px', maxHeight: '20px', minWidth: '30px', minHeight: '20px' }} ><ArrowDropDownIcon />
                                                                                </Button>
                                                                            }
                                                                            {(option.label === intl.formatMessage({ id: "selectpocancellationletter.text" })) &&
                                                                                <Button size="small"
                                                                                    style={{ maxWidth: '20px', maxHeight: '20px', minWidth: '30px', minHeight: '20px' }} ><ArrowDropDownIcon />
                                                                                </Button>
                                                                            }

                                                                        </MenuItem>
                                                                        {option.label === intl.formatMessage({ id: "selectporevisionletter.text" }) && <MenuList>
                                                                            {selectedRevisionList.map((list, index) => (
                                                                                <MenuItem
                                                                                    divider
                                                                                    className={classes.menuListStyle}
                                                                                    key={index}
                                                                                    // selected={index === selectedIndex}
                                                                                    disabled={selectedRow.length > 1}
                                                                                    onClick={() => {
                                                                                        setSelectedRevisionBatchId(list.batchId);
                                                                                        setSelectedRevisionMsgId(list.msgId);
                                                                                        setSelectedRevisionPoNo(list.poNo);
                                                                                    }
                                                                                    }
                                                                                >
                                                                                    {list.batchId, list.amendedTime}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </MenuList>}

                                                                        {option.label === intl.formatMessage({ id: "selectpocancellationletter.text" }) && <MenuList>
                                                                            {selectedCancellationList.map((list, index) => (
                                                                                <MenuItem
                                                                                    divider
                                                                                    className={classes.menuListStyle}
                                                                                    key={index}
                                                                                    // selected={index === selectedIndex}
                                                                                    disabled={selectedRow.length > 1}
                                                                                    onClick={() => {
                                                                                        setSelectedCancellationBatchId(list.batchId);
                                                                                        setSelectedCancellationMsgId(list.msgId);
                                                                                    }
                                                                                    }
                                                                                >
                                                                                    {list.batchId, list.cancellationTime}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </MenuList>}
                                                                    </span>
                                                                </Tooltip>
                                                            ))}
                                                        </MenuList>
                                                    </Menu>
                                                </Grid>}
                                                {((purchaseOrderBPModuleAccess !== undefined ? purchaseOrderBPModuleAccess.editFlag || purchaseOrderBPModuleAccess.createFlag : false) ||
                                                    (poPfOutboundModuleAccess !== undefined ? poPfOutboundModuleAccess.editFlag || poPfOutboundModuleAccess.createFlag : false))
                                                    && (
                                                        <Grid>
                                                            <Tooltip title={<FormattedMessage id="importdlvschedulebutton" />} placement='bottom'>
                                                                <span>
                                                                    <Button size='small' className={classes.refreshToobarStyle} color="primary" variant="outlined"
                                                                  //      disabled={tableData.length <= 0}
                                                                        onClick={(() => {
                                                                            setOpenFileUploadDialog(true);
                                                                            setAnchorE2(null);
                                                                            setAnchorEl(null);
                                                                        })}
                                                                    > <ImportIcon />
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                        </Grid>)}
                                                {(isBusinessPartnerPortal) && (
                                                    <Grid>
                                                        {(<Tooltip title={<FormattedMessage id="exportoscfmdatereport.button.tooltip" />} placement='bottom'>
                                                            <span>
                                                                <Button size='small' className={classes.refreshToobarStyle} color="primary" variant="outlined"
                                                                    disabled={true}
                                                                    component="span"
                                                                    onClick={(() => {
                                                                        requestXLReportExport(dlvScheduleButtonForSAP, true);
                                                                    })}
                                                                    endIcon={isDlvScheduleForSapExportXlRequested ? <CircularProgress color="secondary" size={20} /> : null}
                                                                > <ExportIcon />
                                                                </Button>
                                                            </span>
                                                        </Tooltip>)}
                                                    </Grid>
                                                )}
                                                {(!isBusinessPartnerPortal) && (
                                                    <Grid>
                                                        {(<Tooltip title={<FormattedMessage id="supplierUpldDlvCfm.button" />}
                                                            placement='bottom'>
                                                            <span>
                                                                <Button size='small' className={classes.refreshToobarStyle} color="primary" variant="outlined"
                                                                    component="span"
                                                                    disabled={tableData.length <= 0}
                                                                    onClick={(() => {
                                                                        setOpenDlvyCfmStatDialog(true);
                                                                    })}
                                                                > <CandlestickChartIcon />
                                                                </Button>
                                                            </span>
                                                        </Tooltip>)}
                                                    </Grid>
                                                )}
                                                {(!isBusinessPartnerPortal) && (
                                                    <Grid>
                                                        {(<Tooltip title={<FormattedMessage id="common.tooltip.breadcrumb.downloadReport" />}
                                                            placement='bottom' className={classes.actionButton} onClick={() => {
                                                                setOpenReportDownloadDialog(true);
                                                            }}>
                                                            <span>
                                                                <Button size='small' className={classes.refreshToobarStyle} color="primary" variant="outlined"
                                                                    component="span" disabled={selectedRow.length === 0 ? true : false}
                                                                    onClick={() => {
                                                                        setOpenReportDownloadDialog(true);
                                                                    }}>
                                                                    <CustomReportIcon />
                                                                </Button>
                                                                {/* <IconButton color="secondary" size="large"> */}
                                                                {/* <CustomReportIcon /> */}
                                                                {/* </IconButton> */}
                                                            </span>
                                                        </Tooltip>)}
                                                    </Grid>
                                                )}
                                                <Grid>
                                                    <Tooltip title={<FormattedMessage id="common.button.refresh.tooltip" defaultMessage="Refresh" />} placement='bottom'>
                                                        <span>
                                                            <Button size="small" color="primary" variant="outlined" className={classes.refreshToobarStyle}
                                                                onClick={(() => {
                                                                    setRefreshTable(!refreshTable);
                                                                    setSelectedRow([]);
                                                                    setAnchorE2(null);
                                                                    setAnchorEl(null);
                                                                })}>
                                                                <RefreshIcon />
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={4} lg={4}>
                                                <CommonGridPagination />
                                            </Grid>
                                        </Grid>
                                    );
                                },
                            }}
                            hideFooterSelectedRowCount={true}
                            onPageSizeChange={((pageParams) => {
                                setParamBody(prevState => { return { ...prevState, recordsPerPage: pageParams } })
                            })}
                            onPageChange={((pageParams) => {
                                setParamBody(prevState => { return { ...prevState, pageNo: (pageParams) } })
                            })}
                            pinnedColumns={pinnedColumns}
                            onPinnedColumnsChange={((params) => {
                                var OrderedColumns = columns;
                                if (pinnedColumns.left.length > params.left.length && pinnedColumns.right.length < params.right.length) {
                                    var unpinnedColumns = pinnedColumns.left.filter(x => params.left.indexOf(x) === -1);
                                    var index = OrderedColumns.findIndex(element => element.field === unpinnedColumns[0]);
                                    var reOrderedColumns = OrderedColumns.splice(index, 1);
                                    OrderedColumns.splice(OrderedColumns.length, 0, reOrderedColumns[0]);
                                }
                                else if (pinnedColumns.right.length > params.right.length && pinnedColumns.left.length < params.left.length) {
                                    var unpinnedColumns = pinnedColumns.right.filter(x => params.right.indexOf(x) === -1);
                                    var index = OrderedColumns.findIndex(element => element.field === unpinnedColumns[0]);
                                    var reOrderedColumns = OrderedColumns.splice(index, 1);
                                    OrderedColumns.splice(pinnedColumns.left.length - 1, 0, reOrderedColumns[0]);
                                }
                                else if (pinnedColumns.left.length < params.left.length) {
                                    var index = OrderedColumns.findIndex(element => element.field === params.left[params.left.length - 1]);
                                    var reOrderedColumns = OrderedColumns.splice(index, 1);
                                    OrderedColumns.splice(pinnedColumns.left.length - 1, 0, reOrderedColumns[0]);
                                }
                                else if (pinnedColumns.right.length < params.right.length) {
                                    var index = OrderedColumns.findIndex(element => element.field === params.right[params.right.length - 1]);
                                    var reOrderedColumns = OrderedColumns.splice(index, 1);
                                    OrderedColumns.splice(OrderedColumns.length, 0, reOrderedColumns[0]);
                                }
                                else if (pinnedColumns.right.length > params.right.length) {
                                    var unpinnedColumns = pinnedColumns.right.filter(x => params.right.indexOf(x) === -1);
                                    var index = OrderedColumns.findIndex(element => element.field === unpinnedColumns[0]);
                                    var reOrderedColumns = OrderedColumns.splice(index, 1);
                                    OrderedColumns.splice(-params.right.length, 0, reOrderedColumns[0]);
                                }
                                else if (pinnedColumns.left.length > params.left.length) {
                                    var unpinnedColumns = pinnedColumns.left.filter(x => params.left.indexOf(x) === -1);
                                    var index = OrderedColumns.findIndex(element => element.field === unpinnedColumns[0]);
                                    var reOrderedColumns = OrderedColumns.splice(index, 1);
                                    OrderedColumns.splice(params.left.length - 1, 0, reOrderedColumns[0]);
                                }
                                savePreference("pinned", {
                                    left: params.left,
                                    right: params.right
                                }, OrderedColumns);

                            })}
                            onColumnVisibilityChange={((params) => {
                                if (params.field !== '__check__') {
                                    var visibleColumns = columns;
                                    var index = visibleColumns.findIndex(element => element.field === params.field);
                                    visibleColumns[index].hide = !params.isVisible;
                                    savePreference("visibility", visibleColumns);
                                }
                            })}
                            onColumnOrderChange={((params) => {
                                --params.oldIndex;
                                --params.targetIndex;
                                var OrderedColumns = columns;
                                var reOrderedColumns = OrderedColumns.splice(params.oldIndex, 1);
                                OrderedColumns.splice(params.targetIndex, 0, reOrderedColumns[0]);
                                savePreference("order", OrderedColumns);
                            })}
                            onSortModelChange={(params) => {

                                let sortModel = params[0];
                                var previousParamBody = paramBody;
                                if (sortModel !== undefined && !(previousParamBody.sortDir === sortModel.sort && previousParamBody.sortKey === sortModel.field)) {

                                    setParamBody({ ...paramBody, sortDir: sortModel.sort, sortKey: sortModel.field });
                                    savePreference("sort", params[0]);
                                } else if (sortModel === undefined && !(previousParamBody.sortDir === null && previousParamBody.sortKey === null)) {
                                    setParamBody({ ...paramBody, sortDir: null, sortKey: null });
                                    savePreference("sort", { field: null, sort: null });
                                }
                            }}
                            sortModel={[{
                                field: paramBody.sortKey,
                                sort: paramBody.sortDir,
                            }]}
                            sortingOrder={['desc', 'asc']}
                            filterMode="server"
                        />
                    </div>
                </Paper>
            </Grid >
        </React.Fragment >
    )
}

export default PurchaseOrderForecastOutbound;